import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { CustomerSegment, UserRole } from '@bhops/shared';
import {
  customerMasterApi,
  CUSTOMER_MASTER_STATUS_LABEL,
  CUSTOMER_MASTER_STATUS_COLOR,
  // 개선 9건 차수2c(26-08-04) §5 — 다계약 배지·툴팁이 읽는 계약 요약.
  type CustomerMasterContractSummary,
  type CustomerMasterRow,
  // 개선 9건 차수2f(26-08-04) — 시트의 금액·대수는 고객사 단위 합계 자리에서만 온다.
  type CustomerMasterRowTotals,
  type CustomerMasterStatus,
  type ListCustomerMasterQuery,
} from '@/api/customer-master';
import { customersApi } from '@/api/customers';
import { optionGroupsApi } from '@/api/option-groups';
import { extractApiError } from '@/api/client';
// 추가수정 2차(26-07-27) §2 — 표시 날짜는 KST 정본으로.
// 계획서 §7ⓑ(26-08-21) — CSV 파일명의 '오늘'도 KST(다른 CSV 내보내기와 같은 규칙).
import { kstYmd, kstTodayYmd } from '@/lib/format-date';
// 계약 사실화 1단계 정정6(26-07-29) — '계약 형태' 칸의 문구는 표기 정본에서만 온다.
// 정정9(26-07-29) — 오너 지시로 이 칸은 구독/판매 두 값으로 접는다(접기도 정본 한 벌).
import {
  CONTRACT_REASON_EMPTY,
  foldContractReasonToKind,
  foldContractReasonsToKindSummary,
} from '@/lib/contract-reason';
import { formatKrwOrNone } from '@/lib/amount-or-none';
// 개선 9건 차수2f(26-08-04) — 배정·설치 단계 한국어 표기는 lib 정본 한 벌에서만 온다
// (종전엔 이 파일이 같은 표를 따로 들고 있었다 — 사본이 넷째로 늘던 자리).
import { formatContractHandoff } from '@/lib/contract-handoff';
// 다계약 표시 차수3 정정2(26-08-06) — 네 칸의 기준 판정 · '판매는 반납이 없다'는 문구 ·
// 판매 전용 계약의 반납일 표기는 **전부 lib 정본 한 벌**에서 온다. 종전엔 이 파일이 같은
// 판정과 같은 문구를 자기 사본으로 들고 있었고, 고객사 상세가 사본을 고치지 않아 한 계약을
// 두고 두 화면이 다른 말을 했다(그 사본이 지금 닫는 결함의 경로다 — lib 머리 주석).
import {
  endDateFieldText,
  isSaleOnlyByLines,
  isStatusBasisContract,
  resolveTermBasis,
  termBasisBlankCellsTip,
  termBasisNoReturnExplanation,
  termBasisNoReturnLabel,
  termBasisNoReturnReason,
  STATUS_BASIS_STAGE_TEXT,
  TERM_BASIS_CANDIDATE_RULE_TEXT,
  TERM_BASIS_PICK_RULE_TEXT,
  type TermBasis,
} from '@/lib/term-basis';
// D-Day 표기(값이 없으면 null)도 정본 한 벌 — lib/d-day. 종전엔 이 파일에 사본이 있었다.
import { formatDdayOrNull } from '@/lib/d-day';
import { SortableTh } from '@/components/SortableTh';
import {
  nextColumnSort,
  sortRowsByColumn,
  type ColumnSortKind,
  type ColumnSortState,
  type ColumnSortValue,
} from '@/lib/column-sort';
import { useAuthStore } from '@/stores/auth';
import { canEditSales } from '@/permissions';
import { useToast } from '@/components/Toast';
import { Button, Input, Textarea } from '@/components/Form';
import { Modal } from '@/components/Modal';
import { Pagination } from '@/components/Pagination';
import { MapUrlIcon } from '@/components/MapUrlIcon';
import { invalidateCustomerMasterCascade } from '@/lib/invalidate-customer-master-cascade';
import { CustomerMasterCreateModal } from './CustomerMasterCreateModal';

// docs/plan/customer-master-ux/plan.md — 통합 시트 (Phase 1, read-only).
//
// 한 row = 한 Customer (브랜드/상호). 35+ 컬럼을 가로 스크롤로 표시.
// 그룹 헤더로 status 별 묶음. 검색 / status 필터 / 그룹화 / CSV export.
//
// Phase 2 에서 '+ 신규 등록' 모달 wiring 예정 (현재는 disabled).

const SEGMENT_LABEL: Record<CustomerSegment, string> = {
  UNKNOWN: '미분류',
  FRANCHISE_HQ: '프랜차이즈 본사',
  FRANCHISE_STORE: '프랜차이즈 매장',
  INDEPENDENT_RESTAURANT: '독립 레스토랑',
  HOTEL_RESORT: '호텔 · 리조트',
  CATERING: '케이터링',
  RETAIL_CHAIN: '리테일 체인',
  CAFE: '카페',
  BAKERY: '베이커리',
  CLOUD_KITCHEN: '클라우드 키친',
  EDUCATION: '교육',
  RESEARCH: '연구',
  GOVERNMENT: '공공기관',
  EXPORT_DISTRIBUTOR: '해외 디스트리뷰터',
  INTERNAL_OPS: '사내 운영',
};

const STATUS_ORDER: CustomerMasterStatus[] = [
  'install_done',
  'contracted',
  'install_ready',
  'in_negotiation',
  'return_planned',
  'retired',
  'undefined',
];

// ============================================================
// 머리를 눌러 정렬(2026-10-01 오너 요청) — 칸마다 무엇을 기준으로 줄 세우는가
// ============================================================
//
// 값은 **그 칸이 화면에 보여 주는 값과 같은 자리**에서 읽는다(칸 렌더러 Row 와 같은 함수 —
// resolveTermBasis · rowTotals · contractedRobotCount · returnInfo). 그래야 "왜 이 순서인가"를
// 칸의 글자로 설명할 수 있다. 단계(1번·2번·해제)와 빈 값 처리는 lib/column-sort 정본 한 벌.
//
// 정렬은 **화면에서** 한다: 칸 값(기준 계약·합계)이 화면 쪽 판정이라 서버에 같은 규칙을 한 벌
// 더 적으면 둘이 갈린다. 대신 머리 정렬이 켜지면 목록을 한 번에(상한 200곳) 받아 정렬한 뒤
// 화면에서 페이지를 나눈다 — 한 페이지 안에서만 줄 세우면 2쪽의 값이 1쪽보다 앞서야 하는데도
// 뒤에 남는다. 200곳을 넘으면 표 위 안내가 그 사실을 말한다(cm-colsort-partial-notice).
type ColumnSortKey =
  | 'name'
  | 'location'
  | 'storeCount'
  | 'salesOwner'
  | 'rentalCompany'
  | 'insurance'
  | 'robotType'
  | 'rentalTerm'
  | 'contractQty'
  | 'contractedAt'
  | 'installCompletedAt'
  | 'endDate'
  | 'dday'
  | 'returnedAt'
  | 'subscriptionFee'
  | 'salesPrice'
  | 'deposit'
  | 'subsidy'
  | 'referralFee'
  | 'status';

/** 머리 정렬이 켜졌을 때 한 번에 받아 오는 행 수(서버 pageSize 상한). */
const COLUMN_SORT_FETCH_SIZE = 200;

const DAY_MS = 86_400_000;
const ms = (v: string | null | undefined): number | null => {
  if (!v) return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
};
/** 오늘(한국 날짜)부터 그날까지 남은 날 — 지난 날은 음수. */
const daysFromToday = (v: string | null | undefined): number | null => {
  const ymd = v ? kstYmd(v) : '';
  if (!ymd) return null;
  return Math.round((Date.parse(ymd) - Date.parse(kstTodayYmd())) / DAY_MS);
};

const COLUMN_SORTS: Record<
  ColumnSortKey,
  { kind: ColumnSortKind; price?: boolean; value: (row: CustomerMasterRow) => ColumnSortValue }
> = {
  name: { kind: 'text', value: (r) => r.displayName },
  location: { kind: 'text', value: (r) => r.address || r.region },
  storeCount: { kind: 'number', value: (r) => r.expectedStoreCount ?? r.storeCount },
  // 2026-10-02 오너 요청 — 영업 담당 · 보증 보험 · 모델도 머리 정렬.
  salesOwner: { kind: 'text', value: (r) => r.salesOwnerCode },
  rentalCompany: {
    kind: 'text',
    value: (r) => r.rentalCompany ?? (r.hasAJ === true ? 'O' : r.hasAJ === false ? 'X' : null),
  },
  // 보증 보험 칸(formatBool)과 같은 값 — O 먼저 / X 먼저, 모르는 곳(–)은 늘 뒤.
  insurance: {
    kind: 'flag',
    value: (r) => (r.hasInsurance === true ? 0 : r.hasInsurance === false ? 1 : null),
  },
  // 모델 칸(robotTypeCell)과 같은 글자. 빈 칸('–')은 빈 값으로 본다.
  robotType: {
    kind: 'text',
    value: (r) => {
      const t = robotTypeCell(r).text;
      return t === '–' ? null : t;
    },
  },
  rentalTerm: { kind: 'number', value: (r) => resolveTermBasis(r).facts?.rentalTermMonths },
  contractQty: { kind: 'number', value: (r) => contractedRobotCount(r) },
  contractedAt: { kind: 'date', value: (r) => ms(r.contract?.contractedAt) },
  installCompletedAt: {
    kind: 'date',
    value: (r) => ms(resolveTermBasis(r).facts?.installCompletedAt),
  },
  endDate: { kind: 'date', value: (r) => ms(resolveTermBasis(r).facts?.endDate) },
  dday: { kind: 'imminent', value: (r) => resolveTermBasis(r).facts?.daysRemaining },
  // 회수일 칸과 같은 값 — 확정(returnedAt)이 있으면 그것, 없으면 예정일(returnDateCellValue).
  returnedAt: {
    kind: 'imminent',
    value: (r) => daysFromToday(r.returnInfo?.returnedAt ?? r.returnInfo?.returnScheduledAt),
  },
  // 금액 다섯 칸은 가격 권한이 있을 때만 정렬된다(가려진 값으로 줄을 세우면 순서가 값을 흘린다).
  subscriptionFee: { kind: 'number', price: true, value: (r) => rowTotals(r)?.subscriptionFeeKrw },
  salesPrice: { kind: 'number', price: true, value: (r) => rowTotals(r)?.salesPriceKrw },
  deposit: { kind: 'number', price: true, value: (r) => rowTotals(r)?.depositKrw },
  subsidy: { kind: 'number', price: true, value: (r) => rowTotals(r)?.subsidyKrw },
  referralFee: { kind: 'number', price: true, value: (r) => rowTotals(r)?.referralFeeKrw },
  // 진행 상태 — 상태별 묶음 순서(STATUS_ORDER, '상태별' 그룹화와 같은 순서).
  status: { kind: 'category', value: (r) => STATUS_ORDER.indexOf(r.status) },
};

// 계획서 요구 2a §3·§4(26-08-21) — 상태 필터 select 안의 '기존고객사' 항목이 쓰는 값.
// 상태 enum 이 **아니다**(파생 분류라 status 로 보내지 않는다 — 서버 DTO 도 별도 파라미터
// former 다). 언더스코어 접두는 이 select 의 다른 특수값 관례(AUTO_STATUS_VALUE)와 같다.
const FORMER_FILTER_VALUE = '__former__' as const;

// 추가수정 2차(26-07-27) §1 (결정 ①A) — 기본 100곳. 예전엔 이 값이 유일한
// pageSize 이자 사실상의 상한이라 101곳째부터 목록에서 사라졌다. 이제는 하단
// Pagination 의 초기값일 뿐이고, 운영자가 50/100/200 중에서 고를 수 있다.
const PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [50, 100, 200];

// FB §10 / round5 #1c / round11 (요구 9) — 정렬 모드.
//
// 다계약 표시 차수3(26-08-05) — **기본이 '처음 계약일 순'으로 바뀌었다**(오너 선택). 종전
// 기본이던 등록 최근순은 'registered' 로 선택지에 남는다. 종전엔 기본 모드가 sort 파라미터를
// **보내지 않고** 서버 기본에 기대고 있었는데, 서버 기본이 바뀐 지금 그 침묵은 "화면의 라벨이
// 서버가 실제로 주는 순서와 다를 수 있다"는 뜻이 된다 — 그래서 모드마다 서버 값을 **명시로**
// 보낸다(serverSortFor 한 벌). 라벨이 거짓이 되지 않는 것이 이 명시의 이유다.
//
// 🔴 정정5(26-08-06) — 그 명시에는 **배포 구간의 대가**가 있다: 'contractedAt' 는 이 차수의 api
//   부터 아는 값이라, 콘솔이 먼저 올라간 구간의 구버전 api 는 그 값을 400 으로 거절한다(운영
//   DTO 가 `@IsEnum(['recent','name'])` + ValidationPipe forbidNonWhitelisted). 그때 이 시트는
//   **행 0개**가 됐다 — 정렬 값이 filters 초기값이라 검색·필터로 우회할 수도 없었다.
//   지금은 api 계층이 정렬을 빼고 한 번 다시 받아 시트를 띄우고(customerMasterApi.list),
//   물러났다는 사실을 rejectedSort 로 올려 아래 두 자리가 말한다: 정렬 기준 문구
//   (SORT_REJECTED_BASIS_TEXT)와 표 위 안내(cm-sort-rejected-notice). 정렬 select 는 고른 값을
//   그대로 보여 주므로, 그 두 자리가 침묵하면 화면이 순서를 두고 거짓말을 한다.
//
// 두 갈래가 있고 **키가 다르다**(그래서 라벨도 다른 단어를 쓴다):
//   • 'firstContract' — 서버 정렬. 키는 **이 고객사와 처음 계약한 날**(전 계약 중 가장 이른
//     계약일 = row.firstContractedAt). 계약일을 모르는 고객사는 맨 뒤. 서버가 전 범위를
//     정렬한 뒤 자르므로 페이지 경계까지 정확하다.
//   • 'contractDate' / 'contractDateAsc' — 받아온 페이지 안에서 화면이 재정렬. 키는 시트의
//     '계약일' 칸(= 최신 계약의 계약일). 사업 대시보드 "이번 달 신규 계약" 카드가
//     ?sort=contractDate 로 이 모드를 켠다 — 그 카드가 찾는 것이 **가장 최근에 맺은 계약**이다.
// 계약이 여럿인 고객사에서 두 키가 실제로 다르므로 **라벨이 서로 다른 단어를 쓴다**
// ('처음 계약일' vs '최근 계약일' vs '시트 계약일'). 종전엔 정렬 옆에 기준 문구도 적었으나
// 26-08-06 오너 요구로 없앴다 — 이제 라벨이 그 일을 혼자 한다.
//
// 기본 정렬 개편(26-08-06 오너 요구) — **기본이 'latestContract'(최근 계약일 순)로 바뀌었다.**
//   • 'latestContract' — **서버 정렬**(신설). 키는 **최신 계약의 계약일** 내림차순 = 시트
//     '계약일' 칸이 보여 주는 그 날짜. 위에 두 층이 얹힌다(오너 확정 순서):
//       1층 **계약일 미입력**(계약이 없거나 최신 계약의 계약일이 비어 있음) ·
//       2층 진행 상태 **'계약 체결중'(in_negotiation)** · 그 아래가 계약일 내림차순.
//     두 층은 **겹친다**(초안 계약은 계약일이 없는 것이 정상). 비교가 사전식이라 1층 안에서도
//     체결중이 위로 뜬다 — 그 성질과 운영 실측 근거는 서버 비교 함수 주석에 있다.
//     ⚠ 계약일 미입력이 **상단**인 것은 이 모드뿐이다 — 'contractedAt'(처음 계약일 순)은
//       종전 관례대로 맨 뒤에 둔다(두 모드가 다른 질문에 답한다).
//     서버가 전 범위를 정렬한 뒤 자르므로 **페이지 경계까지 정확하다.**
//   ⚠ 'contractDate'(시트 계약일 ↓)를 기본으로 삼지 **않은** 이유: 그 모드는 받아온 페이지 안에서만
//     재정렬한다(서버엔 'recent' 를 요청). 기본으로 쓰면 1페이지가 "최근 **등록**한 100곳"이라
//     전 범위에서 가장 최근에 계약한 곳이 2페이지로 밀린다(운영 ~113곳이라 실제로 어긋난다).
//     아래 serverSortFor 주석이 예견해 둔 "서버가 최신 계약일로 전 범위를 정렬" 이 그것이고,
//     이번에 그 값을 만들었다.
type SortMode =
  | 'latestContract'
  | 'firstContract'
  | 'registered'
  | 'name'
  | 'contractDate'
  | 'contractDateAsc';

/**
 * 이 모드가 서버에 보낼 정렬 값.
 *
 * 계약일 2종은 화면이 **받아온 페이지 안에서만** 재정렬하므로, 서버가 어느 100곳을 실어
 * 주는지가 그 모드의 결과를 정한다. 그래서 이 두 모드는 **등록 최근순**을 요청한다:
 *   대시보드 "이번 달 신규 계약" 카드가 이 모드를 켜고, 그 카드가 찾는 고객사는 방금 계약한
 *   곳이다. 기본 정렬('처음 계약일' 오름차순)을 요청하면 1페이지가 **가장 오래 전에 계약한**
 *   100곳이 되어, 고객사 수가 한 페이지를 넘는 순간(운영 ~110곳) 이번 달 계약이 2페이지로
 *   밀려 카드를 눌러도 보이지 않는다. 종전 화면은 이 모드에서 sort 를 보내지 않아 서버 기본
 *   (등록 최근순)을 받았으므로, 'recent' 를 명시하는 것이 그 동작을 글자로 적은 것이다.
 * 정확한 답은 서버가 '최신 계약일' 로도 전 범위를 정렬해 주는 것이지만, 그것은 새 정렬 값을
 * 하나 더 만드는 일이라 이 차수에서 하지 않았다(그때 이 주석이 근거다).
 */
function serverSortFor(mode: SortMode): NonNullable<ListCustomerMasterQuery['sort']> {
  if (mode === 'name') return 'name';
  if (mode === 'registered') return 'recent';
  if (mode === 'contractDate' || mode === 'contractDateAsc') return 'recent';
  if (mode === 'firstContract') return 'contractedAt';
  return 'latestContract';
}

/** 정렬 선택지 라벨 — 두 계약일 키를 다른 단어로 부른다(같은 단어면 같은 키로 읽힌다). */
const SORT_LABEL: Record<SortMode, string> = {
  latestContract: '최근 계약일 순 (기본)',
  firstContract: '처음 계약일 순',
  registered: '등록 최근순',
  name: '가나다순',
  contractDate: '시트 계약일 최근순',
  contractDateAsc: '시트 계약일 오래된순',
};

/**
 * 다계약 표시 차수3 정정5(26-08-06) — 이 서버가 그 정렬 값을 **거절해서** 정렬 없이 받아온 때의
 * 안내. 화면이 고른 정렬을 select 에 그대로 보여 주므로, 이 자리가 침묵하면 화면이 순서를 두고
 * 거짓말을 한다(운영자는 '최근 계약일 순'인 줄 아는데 실제로는 서버 기본 순서다).
 *
 * ⚠ **정상 동작 시의 기준 문구(SORT_BASIS_TEXT)는 26-08-06 오너 요구로 없앴지만, 이 문구는
 *   남긴다.** 둘은 다른 물건이다 — 저건 "지금 이렇게 정렬 중"이라는 설명이고(오너: 필요 없음),
 *   이건 **"고른 정렬이 먹지 않았다"는 사고 신호**다. 배포 구간(콘솔이 api 보다 먼저 올라간 때)에만
 *   뜨고, 지우면 적대 감사가 닫은 결함이 그대로 다시 열린다.
 */
const SORT_REJECTED_BASIS_TEXT = '이 서버가 모르는 정렬이라 서버 기본 순서로 보여 주는 중';

const SORT_OPTIONS: readonly SortMode[] = [
  'latestContract',
  'firstContract',
  'registered',
  'name',
  'contractDate',
  'contractDateAsc',
];

// 계약일 비교자 — null/빈 값은 정렬 방향과 무관하게 항상 뒤로.
function compareByContractDate(
  a: CustomerMasterRow,
  b: CustomerMasterRow,
  direction: 'asc' | 'desc',
): number {
  const av = a.contract?.contractedAt ?? null;
  const bv = b.contract?.contractedAt ?? null;
  if (av === bv) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  // ISO 문자열은 사전식 비교가 곧 시간 순서.
  if (direction === 'desc') return av < bv ? 1 : -1;
  return av < bv ? -1 : 1;
}

// canSeePrice helper — VIEWER / FIELD 등은 가격 마스킹.
// shared/enums.ts 의 UserRole 에 SALES 가 존재하지 않음 (SALES_MANAGER 로
// 일원화됨). 기존 잘못된 참조 제거.
// 배열 리터럴의 좁혀진 타입이 `.includes(role: UserRole)` 와 충돌하는
// TS 한계는 readonly literal-tuple 로 회피.
const PRICE_VISIBLE_ROLES: readonly UserRole[] = [
  UserRole.ADMIN,
  UserRole.MANAGER,
  UserRole.SALES_MANAGER,
];
function canSeePrice(role: UserRole | undefined): boolean {
  if (!role) return false;
  return PRICE_VISIBLE_ROLES.includes(role);
}

// 계약 사실화 1단계 정정8(26-07-29) — 금액 표기 정본(lib/amount-or-none) 한 벌만 쓴다.
// 종전 이 함수는 **0 을 '₩0' 으로 찍었다**: 판매 계약의 월 구독료 칸은 NOT NULL 이라
// '없음'이 0 으로 저장되므로, 판매 계약의 '총 구독료'가 이 목록에서 ₩0 으로 보였다
// (같은 계약을 계약 상세는 "월 구독료 없음"이라 말한다 — 두 화면이 서로 다른 말).
// 그룹 머리의 '누적' 도 같은 규칙을 따른다 — 구독 금액이 하나도 없는 그룹의 누적을
// '₩0' 이라 적으면 0원 누적이라는 뜻이 되므로 '없음' 표기가 사실이다.
// ⚠ 마스킹이 앞, 0 접기가 뒤 — 호출부는 모두 `priceVisible ? formatKRW(...) : '—'` 모양.
function formatKRW(n: number | null | undefined): string {
  return formatKrwOrNone(n);
}

// 추가수정 2차(26-07-27) §2 — 브라우저 로캘(PC 시간대) 대신 KST 고정.
// 마스터 시트는 목록이므로 날짜만(결정 ②A). kstYmd 가 null/빈값/파싱불가를
// 모두 null 로 돌려주므로 try/catch 없이 폴백 한 줄로 정리된다.
function formatDate(s: string | null | undefined): string {
  return kstYmd(s) ?? '–';
}

function formatBool(v: boolean | null): string {
  if (v === null) return '–';
  return v ? 'O' : 'X';
}

// 개선 9건 차수2c(26-08-04) §5 — 다계약 안내는 배정 단계까지 적는다.
//
// 시트의 반납일·D-day·진행 상태 칸은 계약 **한 건**만 말할 수 있다. 계약이 여럿인
// 고객사에서 그 사실을 숨기면 "새로 등록한 계약이 배정 대기인데 시트는 옛 계약의
// 설치 완료만 보여 주는" 조용한 누락이 된다. 그래서 배지 안내가 전부 적는다:
// 계약번호 · 계약 형태 · 계약일 · 도입일 · 반납일 · D-day · 배정 단계 · 대수 +
// 어느 계약이 어느 칸의 기준인지 + 어느 계약을 합계에 셌는지.
// 단계 문구는 lib/contract-handoff 정본(formatContractHandoff)이 만든다.

// ============================================================
// 개선 9건 차수2e(26-08-04) ⑤ — 이 시트의 칸마다 '어느 계약을 말하는가'
// ============================================================
//
// 계약이 2건 이상인 고객사에서 이 시트의 칸은 세 갈래로 갈린다(서버 응답이 이미 그렇게
// 갈라져 있다 — apps/api/src/customer-master/customer-master.row.ts 머리 주석):
//   • 가장 임박한 만기 계약 — 반납일·D-Day·진행 상태 (row.soonestContract)
//   • 최신 계약(= 편집 창이 고치는 계약) — 계약 형태·계약기간·계약일·도입일 (row.contract)
//   • 전 계약 합 — 금액 5칸 + 계약 대수
// 종전엔 반납일 칸의 '계약 N건' 배지가 첫 갈래만 말했다. 나머지 두 갈래는 아무 표시가
// 없어서, 오너가 계약 카드에서 겪은 것과 같은 오해가 시트에서도 가능했다: 판매 계약의
// 계약일 옆 도입일이 어느 계약 것인지 알 수 없고, 총 구독료 680,000 이 어느 계약의
// 금액인지 알 수 없다(0=없음 접기 때문에 종전엔 그 칸이 비어 있었다).
//
// 안내문이 칸 이름을 부르므로 **열 머리와 안내문이 같은 문구 한 벌**을 쓴다. 두 곳에 따로
// 적으면 열 이름을 고친 날 안내문만 옛 이름을 말한다(그 드리프트를 막는 시험이
// CustomerMasterPage.test.tsx '안내가 부르는 칸 이름은 모두 실제 열 머리에 있다').
const COLUMN_LABEL = {
  // '모델' 칸도 정본 이름을 쓴다 — 배지 안내가 이 이름을 부르므로(아래 UNION_COLUMNS)
  // 열 머리에 리터럴로 따로 적혀 있으면 이름을 고친 날 안내문만 옛 이름을 말한다.
  robotType: '모델',
  contractType: '계약 형태',
  rentalTerm: '계약기간',
  contractQty: '계약 대수',
  contractedAt: '계약일',
  installCompletedAt: '도입일',
  endDate: '반납일',
  dday: 'D-Day',
  // 계획서 요구 2a §2(26-08-21) — 회수일. 자리는 반납일·D-Day 바로 옆(계약 수명주기 묶음
  // — 화면 규칙 절). CSV 머리도 이 정본을 쓴다(customerMasterCsvHeader).
  returnedAt: '회수일',
  totalSubscriptionFee: '총 구독료',
  totalSalesPrice: '총 판매가',
  deposit: '보증금',
  subsidy: '지원금',
  referralFee: '소개비',
  status: '진행 상태',
} as const;

/**
 * 다계약 표시 차수3(26-08-05) — **기준 계약**(먼저 끝나는 계약 · 판매 전용 제외)을 보는 네 칸.
 *
 * 오너 사양(26-08-05): "판매는 고객사 소유라 반납일이 따로 필요 없다. 따라서 표시되는
 * 도입일 · 반납일 · 계약기간 · D-Day 는 구독 계약 기준이다." 그래서 종전에 최신 계약을
 * 보던 계약기간·도입일이 이 목록으로 옮겨 왔다 — 네 칸이 **한 계약**을 말한다.
 * 판매만 있는 고객사는 네 칸이 '–' 이고(오너 확인), 왜 비었는지는 배지 안내가 말한다.
 */
const TERM_BASIS_COLUMNS: readonly string[] = [
  COLUMN_LABEL.rentalTerm,
  COLUMN_LABEL.installCompletedAt,
  COLUMN_LABEL.endDate,
  COLUMN_LABEL.dday,
];
/**
 * **진행 상태** 한 칸 — 위 네 칸과 목록이 갈린다.
 *
 * 다계약 표시 차수3(26-08-05) — 판매만 있는 고객사는 네 칸이 '–' 인데도 이 칸은 그 판매
 * 계약의 단계를 말한다(배정·설치·인수는 판매에도 일어난다). 그래서 서버가 이 칸의 기준
 * 계약을 따로 표시해 내리고(contracts[].isStatusBasis) 화면도 목록을 따로 둔다.
 */
const STATUS_BASIS_COLUMNS: readonly string[] = [COLUMN_LABEL.status];
/**
 * 최신 계약(= 편집 창이 고치고 저장이 닿는 계약)을 보는 칸 — 이제 '계약일' 하나다.
 *
 * 다계약 표시 차수3(26-08-05) — 종전 이 목록에 있던 모델·계약 형태는 **전 계약 합집합**
 * (아래 UNION_COLUMNS)으로, 계약기간·도입일은 **기준 계약**(위 TERM_BASIS_COLUMNS)으로
 * 옮겨 갔다. 이 세 목록이 사실과 갈리지 않게 지키는 시험이 서버 원본을 읽어 대조한다
 * (CustomerMasterPage.test.tsx '★ 드리프트 — …').
 */
const PRIMARY_BASIS_COLUMNS: readonly string[] = [COLUMN_LABEL.contractedAt];
/**
 * 센 계약 **전부**를 합쳐 말하는 칸 — 금액 5칸 · 계약 대수 · 모델 · 계약 형태.
 *
 * 다계약 표시 차수3(26-08-05) — 모델·계약 형태가 여기 들어왔다(종전엔 최신 계약 1건의 줄만
 * 봐서, 추가 판매를 새 계약으로 등록한 고객사의 모델 칸이 그 판매 계약만 말했다). 값은
 * 서버가 합친 것을 그대로 읽는다(row.totals) — 화면이 다시 합치지 않는다. 어느 계약을
 * 합쳤는지는 totals.basis · countedContractIds 가 말하고, 그래서 이 여덟 칸이 **같은 계약
 * 집합**을 말한다(대수는 2인데 모델이 3종 보이는 어긋남이 생기지 않는다).
 */
const UNION_COLUMNS: readonly string[] = [
  COLUMN_LABEL.robotType,
  COLUMN_LABEL.contractType,
  COLUMN_LABEL.totalSubscriptionFee,
  COLUMN_LABEL.totalSalesPrice,
  COLUMN_LABEL.deposit,
  COLUMN_LABEL.subsidy,
  COLUMN_LABEL.referralFee,
  COLUMN_LABEL.contractQty,
];

/** 칸 이름 목록을 사람이 읽는 문구로. 가운뎃점은 시트의 다른 요약과 같은 글자다. */
function joinColumnNames(names: readonly string[]): string {
  return names.join(' · ');
}

/** 금액 열 머리 — 가격 열람 권한이 없으면 잠금 표기를 붙인다(종전과 같은 글자). */
function lockableColumn(label: string, priceVisible: boolean): string {
  return priceVisible ? label : `${label} (잠금)`;
}

// 계약기간 표기 정본 한 벌 — 시트의 계약기간 칸과 네 칸 툴팁이 같은 글자를 쓴다.
// 0개월은 값이 아니라 미입력과 같은 취급(종전 `? :` 판정 그대로 옮겼다).
function formatRentalTerm(months: number | null | undefined): string {
  return months ? `${months}개월` : '–';
}

/**
 * 계획서 요구 2a §2(26-08-21) — '회수일' 칸의 표기 한 벌. 시트 칸과 CSV 가 같은 글자를 쓴다
 * (두 벌이면 CSV 만 옛 규칙으로 남는다).
 *   · 확정 — returnInfo.returnedAt(서버가 '나가 있는 로봇 0 + 기록 있음'일 때만 내린다).
 *   · 예정 — 확정이 없고 returnScheduledAt 이 있으면 `예정 2026.09.01`. 흐린 톤은 화면 몫이라
 *     scheduled 플래그로 돌려준다(CSV 에는 톤이 없고 글자 '예정 …' 이 그 사실을 말한다).
 *   · 둘 다 없으면 '–' — 지어내지 않는다. 구버전 서버(returnInfo 자체가 없음)도 '–' 인데,
 *     그 사실은 칸이 아니라 표 위 안내가 말한다(조용한 빈칸 금지 — 화면 규칙 절).
 */
export function returnDateCellValue(info: CustomerMasterRow['returnInfo']): {
  text: string;
  scheduled: boolean;
} {
  if (info?.returnedAt) return { text: formatDate(info.returnedAt), scheduled: false };
  if (info?.returnScheduledAt) {
    return { text: `예정 ${formatDate(info.returnScheduledAt)}`, scheduled: true };
  }
  return { text: '–', scheduled: false };
}

/**
 * 계획서 요구 2a §1(26-08-21) — 반납일 칸의 **만료 배지** 판정 한 벌(시트 칸 · CSV 공용).
 *
 * 정본은 기준 계약(row.soonestContract)의 status === 'EXPIRED' **정확 일치** 하나다 —
 * 매일 새벽 크론이 찍는 값이라 날짜 계산과 다른 말을 할 수 없다(계획서 요구 2a §1).
 * ⚠ '비 ACTIVE' 같은 느슨한 판정을 쓰면 안 된다: 계약이 전부 해지된 고객사의 폴백에서
 *   soonestContract.status 가 'TERMINATED' 로 올 수 있고, 해지는 만료가 아니다(해지에
 *   이 배지를 달면 운영자가 갱신 대상으로 잘못 읽는다). 그 경계는 시험이 못박는다.
 */
export function isEndDateExpired(row: Pick<CustomerMasterRow, 'soonestContract'>): boolean {
  return row.soonestContract?.status === 'EXPIRED';
}

// ============================================================
// 다계약 표시 차수3(26-08-05) — 네 칸(계약기간·도입일·반납일·D-Day)이 보는 계약
// ============================================================
//
// 오너 사양: 판매는 고객사 소유라 반납이라는 사건이 없으므로 이 네 칸은 **판매 전용이 아닌
// 계약**을 따르고, 그런 계약이 여럿이면 먼저 끝나는 계약을 따라간다. 판정은 서버 한 벌이고
// (customer-master.row pickTermBasisContract) 화면은 그 **결과와 이유**를 읽는다 —
// 화면이 계약 목록을 돌며 판매 전용인지 다시 판정하면 판정이 두 벌이 되고, 그 순간
// 시트의 반납일 칸과 갱신 임박 필터가 서로 다른 계약을 말한다.
//
// 다계약 표시 차수3 정정2(26-08-06) — 그 판정(resolveTermBasis)과 폴백의 갈림길 근거는
// 이제 **lib/term-basis 한 벌**에 있다. 이 파일이 들고 있던 사본을 지웠다: 고객사 상세도
// 같은 규칙을 읽는데, 사본이 있으면 한쪽만 고쳐져 같은 고객사를 두 화면이 다르게 말한다
// (실제로 그렇게 갈렸다 — lib 머리 주석의 🔴).

// ============================================================
// 개선 9건 차수2f(26-08-04) — 시트의 금액·대수는 **합계 자리 한 곳**에서만 읽는다
// ============================================================
//
// 앞 차수는 합계를 `row.contract` 의 금액 5칸에 덮어썼고 대수는 화면이 `row.contracts`
// 전부를 더했다. 둘 다 틀렸다:
//   • row.contract 는 편집 창이 프리필하고 저장이 닿는 **그 계약**이다. 거기 합계가
//     들어가 있으면 다른 계약의 보증금이 이 계약에 새로 생긴다(저장 사고).
//   • 전부 더하면 해지·만료·미체결 계약의 금액·대수가 현재 값으로 섞인다. 갱신한
//     고객사는 예외 없이 걸린다(만기는 자동으로 만료 표시가 찍힌다): 옛 계약 월
//     680,000 + 갱신 계약 월 300,000 을 980,000 이라고 말했다.
// 그래서 서버가 뜻을 두 자리로 나눴고(row.contract = 그 계약 / row.totals = 합계),
// **유효(진행 중) 계약만** 센다. 진행 중 계약이 하나도 없으면 최신 계약 한 건으로
// 폴백하고 무엇을 셌는지(basis) 함께 내린다.
//
// ⚠ 합계 자리가 없으면 **옛 계산으로 되돌리지 않는다.** 옛 계산이 위 두 결함이다 —
//   틀린 숫자보다 빈 칸이 낫고, 빈 칸인 이유는 화면이 말한다(TotalsMissingNotice).

/** 이 행의 합계(없으면 null). 화면 전체가 이 한 함수로만 합계에 닿는다. */
function rowTotals(row: CustomerMasterRow): CustomerMasterRowTotals | null {
  return row.totals ?? null;
}

/**
 * 합계를 **받아야 하는데 못 받은** 행인가.
 *
 * 계약이 아예 없는 고객사는 합계도 없는 것이 정상이라(금액·대수 칸이 종전에도 '–')
 * 여기서 세지 않는다. 계약이 있는데 합계가 없으면 그것은 서버가 아직 이 칸을 내리지
 * 않는 상태다 — 그 사실을 화면이 말해야 한다(조용히 비우지 않는다).
 */
function totalsMissing(row: CustomerMasterRow): boolean {
  return !!row.contract && !row.totals;
}

/**
 * 개선 9건 차수2f(26-08-04) — '계약 대수' 칸.
 *
 * 합계 자리의 대수를 그대로 읽는다. 종전엔 화면이 row.contracts 전부를 더해서 해지·만료
 * 계약의 대수까지 셌다(그 전엔 최신 계약 1건만 세어 구독 1대 + 추가 판매 1대인 고객사가
 * '1대'로 보였다). 판정 함수는 서버 한 벌이고 화면은 세지 않는다.
 *
 * @returns 합계가 없으면 null(화면은 '–' + 왜 비었는지 안내). 0 은 접지 않는다 —
 *          대수 0 은 사실이다.
 */
function contractedRobotCount(row: CustomerMasterRow): number | null {
  return rowTotals(row)?.contractQty ?? null;
}

/**
 * 계약 사실화 1단계 정정6(26-07-29) — '계약 형태' 칸.
 *
 * **이 칸이 실제 데이터 정리 동선이다.** 계획 §1 은 이미 '구독'으로 뭉개져 저장된 판매
 * 건(백암한옥구이 ZEPHYR 등)을 "영업이 고객사 목록을 한 번 훑으며 확인·수정"하기로
 * 했는데, 이 목록에는 계약 형태를 보여 주는 칸이 아예 없어 훑을 자리가 없었다. 서버는
 * 그 값을 이미 내려주고 있었고(row.contract.contractType) 읽는 화면이 하나도 없었다.
 * 이제 영업이 아는 판매 고객사가 '구독'으로 표시된 것을 목록에서 발견하고, 그 자리에서
 * 연필(편집 모달)을 눌러 고칠 수 있다.
 *
 * 계약 사실화 1단계 정정9(26-07-29) — 오너 지시: **"구분은 구독과 판매로만 표시되면
 * 됩니다."** 그래서 이 칸은 파일럿·시연·행사·사내·금융리스를 전부 '구독'으로 접는다
 * (접기 규칙 정본 = lib/contract-reason.foldContractReasonToKind /
 * foldContractReasonsToKindSummary. 여기서 새 접기 함수를 만들지 않는다).
 *
 * ⚠ **접는 것은 이 화면뿐이다. 저장도 감사 로그도 7값 원문 그대로다.**
 * 접는 자리를 화면으로 고른 근거 — 서버가 내리는 요약 문자열
 * (row.contract.contractType = customer-master.row deriveContractTypeSummary)은 **같은 값이
 * 감사 스냅샷에도 실린다**(customer-master.service projectForAudit). 감사 로그는 무엇이
 * 실제로 바뀌었는지 적는 자리라 접으면 안 된다 — 접었다면 파일럿→구독 변경이
 * '구독 → 구독'(= 없던 일)으로 기록된다. 그래서 서버 요약은 원문을 유지하고
 * (감사 로그 화면은 7값 표기 formatContractTypeSummary 로 번역한다) 이 목록만 접는다.
 *
 * 접기의 입력은 **줄의 원문**(row.robots[].reason + quantity)이다. 요약 문자열을 되파싱
 * 하지 않는다 — 파서가 두 벌이 되고, 요약은 이미 접기 손실이 없는 원문을 화면이 그대로
 * 들고 있으므로 되파싱할 이유가 없다(둘 다 같은 latestContract.lines 에서 나온다).
 * 대수 기준으로 세므로 혼합은 '판매 1 · 구독 2'(줄 수가 아니라 대수 — 서버 요약과 같은
 * 셈법).
 *
 * 모르는 값이 **하나라도** 섞이면 접지 않고 서버 요약 **원문을 그대로** 보여 준다.
 * 접기 정본은 모르는 값을 조용히 버리므로(그 값만 빠진 요약이 나온다) 여기서 막는다 —
 * 서버가 새 enum 값을 먼저 내려주는 날의 최악이 '영문이 보인다'(오늘과 같은 상태)여야
 * 하고, 있는 줄이 사라지거나 틀린 한국어로 뭉개지는 일은 없어야 한다.
 *
 * 개선 9건 차수2e(26-08-04) ⑤ — 이 호출 규약을 **한 벌**로 뽑았다. 다계약 배지 안내가
 * 계약별 형태를 같은 어휘로 말해야 하는데(그 안내는 계약마다 다른 줄을 본다), 규약을
 * 두 곳에 적으면 시트 칸과 안내가 같은 계약을 두고 다른 단어를 말한다.
 */
function foldContractTypeForDisplay(
  summary: string | null | undefined,
  lines: ReadonlyArray<{ reason: string; quantity: number }>,
): string {
  // 대수만큼 펼쳐 넘긴다 — 접기 정본은 배열의 원소 수로 센다.
  // ⚠ 같은 3줄이 고객사 상세의 계약 카드(pages/customers/CustomerDetailPage
  // contractKindLine)에도 있다. 규칙 자체(7값→2값 · 혼합 요약)는 정본 한 벌이고 여기
  // 있는 것은 '대수만큼 펼쳐 넘기고 모르는 값이면 접지 않는다'는 호출 규약이다.
  // 정본(lib/contract-reason)이 줄 배열({reason, quantity}[])을 직접 받게 되면 두 곳이
  // 한 줄로 줄어든다 — 그 파일은 이번 차수에 수정 금지라 호출부에 둔다.
  const reasons = lines.flatMap((rb) =>
    Array.from({ length: Math.max(0, rb.quantity) }, () => rb.reason),
  );
  if (reasons.length > 0 && reasons.every((r) => foldContractReasonToKind(r) !== null)) {
    return foldContractReasonsToKindSummary(reasons) ?? CONTRACT_REASON_EMPTY;
  }
  // 줄이 없거나(요약도 null) 모르는 값이 섞인 경우 — 원문 폴백.
  return summary || CONTRACT_REASON_EMPTY;
}

/**
 * 다계약 표시 차수3 정정(26-08-06) — 합집합 칸 하나의 **값 + 그 값이 어디서 왔는가**.
 *
 * 왜 한 함수가 둘을 같이 내는가 — 두 칸(모델·계약 형태)은 서버가 합집합을 내리지 않으면
 * 종전 값(최신 계약 1건)으로 폴백한다. 그런데 배지 꼬리말은 그 갈래를 모르고 이 칸들을
 * '전 계약을 합쳐 말하는 칸'이라고 단정했다(UNION_COLUMNS 는 모듈 상수라 언제나 둘을
 * 포함한다) — 콘솔과 API 를 **따로 배포**하는 지금 런북에서 콘솔이 먼저 올라간 구간에는
 * 반드시 생기는 창이고, 그때 화면은 최신 계약 1건의 값을 보여 주면서 "전 계약을 합쳤다"고
 * 거짓을 말했다. 폴백을 결정하는 자리와 그 사실을 말하는 자리가 갈리면 그 거짓이 다시
 * 열리므로, 폴백을 고른 **그 자리**가 어느 값을 골랐는지 함께 돌려준다.
 *   • union: true  — 합계 자리(row.totals)의 합집합 값이다.
 *   • union: false — 이 응답에 합집합이 없어 **최신 계약 1건**만 보고 있다.
 */
type UnionCellValue = { text: string; union: boolean };

/**
 * '계약 형태' 칸 — 다계약 표시 차수3(26-08-05): **전 계약 합집합**을 접는다.
 *
 * 종전엔 최신 계약(row.contract)의 줄만 접어서, 추가 판매를 새 계약으로 등록한 고객사의
 * 이 칸이 그 판매 계약만 말했다(슭돈 이대역본점: 구독 계약이 살아 있는데도 '판매').
 * 입력은 서버가 합친 줄(totals.robotLines)이다 — **화면이 어느 계약의 줄을 합칠지 다시
 * 판정하지 않는다.** 판정이 두 벌이 되면 이 칸과 금액·대수 칸이 서로 다른 계약 집합을 말한다.
 *
 * 구버전 서버(합집합을 내리지 않는다)에서는 종전대로 최신 계약의 줄을 접는다 — 칸을 비우는
 * 것보다 옛 값이 낫다. 어느 쪽을 보고 있는지는 **이 함수가 돌려주는 union** 이 말하고,
 * 배지 꼬리말이 그 값으로 목록을 가른다(정정 26-08-06: 종전엔 그 갈래가 없었다).
 */
function contractTypeCell(row: CustomerMasterRow): UnionCellValue {
  const totals = rowTotals(row);
  // 합집합을 내리는 서버인가 — robotLines 의 **유무**로 가른다(줄이 없는 고객사도 []를
  // 받으므로, 값의 참·거짓으로 가르면 그 고객사가 구버전으로 오판된다).
  if (totals && totals.robotLines !== undefined) {
    return {
      text: foldContractTypeForDisplay(totals.contractType, totals.robotLines),
      union: true,
    };
  }
  return {
    text: foldContractTypeForDisplay(row.contract?.contractType, row.robots ?? []),
    union: false,
  };
}

/**
 * '모델' 칸 — 다계약 표시 차수3(26-08-05): **전 계약 합집합**('GEN1_D1 × 1, ZEPHYR × 1').
 *
 * 서버가 합친 값을 그대로 옮긴다(화면에 합집합 셈법이 없다 — 계산은 서버 한 벌). 서버는 같은
 * 값을 row.robotTypeSummary 에도 별칭으로 싣는다: 그 칸이 시트가 종전에 읽던 자리라, 콘솔이
 * 먼저 배포되든 서버가 먼저 배포되든 이 칸이 옛 값(최신 계약 1건)으로 남지 않게 한 것이다.
 * 그래서 합계 자리를 먼저 읽고 없으면 별칭으로 폴백한다 — 두 값은 새 서버에서 같다.
 *
 * 폴백했다는 사실은 union: false 로 말한다(정정 26-08-06) — 별칭은 구버전 서버에서 최신 계약
 * 1건의 값이므로, 그 값을 보여 주면서 '전 계약 합'이라고 적으면 화면이 거짓을 말한다.
 */
function robotTypeCell(row: CustomerMasterRow): UnionCellValue {
  const union = rowTotals(row)?.robotTypeSummary;
  if (union !== undefined) return { text: union || '–', union: true };
  return { text: row.robotTypeSummary || '–', union: false };
}

// ============================================================
// 계획서 구현 노트 §7ⓑ(26-08-21) — CSV 내보내기 (파일 머리 주석이 약속만 하고 없던 기능)
// ============================================================
//
// 이 파일 머리 주석은 처음부터 "CSV export" 를 적어 두었지만 실제 구현은 어디에도 없었다
// (계획서 §7ⓑ 는 그 주석을 근거로 '회수일 열을 CSV 에도 등록'을 지시했다). 이번에 실물을
// 만든다 — **지금 보이는 행(현재 페이지·필터·정렬)** 을 시트 칸과 같은 표기 정본으로 내보낸다.
//   · 머리는 열 머리와 같은 글자다(customerMasterCsvHeader ↔ thead 드리프트 시험이 지킨다 —
//     한쪽에만 칸을 더하면 시험이 깨진다).
//   · 값은 시트 칸과 같은 함수에서 온다(robotTypeCell · returnDateCellValue …) — CSV 가
//     화면과 다른 말을 하면 운영자가 어느 쪽이 사실인지 알 수 없다.
//   · 가격 마스킹이 화면과 같은 규칙으로 앞선다 — 화면에서 '—' 인 값이 파일로 새면 안 된다.
//   · 만료 배지는 CSV 에선 색을 쓸 수 없으므로 반납일 값 뒤에 ' (만료)' 글자로 남는다
//     (색만으로 뜻을 전하지 않는다는 화면 규칙과 같은 이유).

// 셀 이스케이프 — BusinessDashboardPage · FleetTrackerPage · EcnCatalogPage 와 같은 규칙의
// 네 번째 사본이다. 정본 추출(lib)은 세 페이지를 함께 고치는 별도 차수의 몫이라 여기서는
// 관례를 따른다(먼저 있던 세 벌을 이 차수가 건드리지 않는다).
function escapeCsvCell(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** CSV 머리 — 열 머리와 같은 글자(잠금 표기까지). '관리' 열은 데이터가 아니라 뺀다. */
export function customerMasterCsvHeader(priceVisible: boolean): string[] {
  return [
    'No.',
    '상호',
    '위치',
    '매장 수',
    '영업 담당',
    '렌탈사',
    '보증 보험',
    COLUMN_LABEL.robotType,
    COLUMN_LABEL.contractType,
    COLUMN_LABEL.rentalTerm,
    COLUMN_LABEL.contractQty,
    COLUMN_LABEL.contractedAt,
    COLUMN_LABEL.installCompletedAt,
    COLUMN_LABEL.endDate,
    COLUMN_LABEL.dday,
    COLUMN_LABEL.returnedAt,
    lockableColumn(COLUMN_LABEL.totalSubscriptionFee, priceVisible),
    lockableColumn(COLUMN_LABEL.totalSalesPrice, priceVisible),
    lockableColumn(COLUMN_LABEL.deposit, priceVisible),
    lockableColumn(COLUMN_LABEL.subsidy, priceVisible),
    lockableColumn(COLUMN_LABEL.referralFee, priceVisible),
    COLUMN_LABEL.status,
    '비고',
  ];
}

/** 한 행 — 각 칸이 시트의 그 칸과 같은 정본 함수를 부른다(따로 계산하지 않는다). */
function customerMasterCsvRow(row: CustomerMasterRow, priceVisible: boolean): string[] {
  const termFacts = resolveTermBasis(row).facts;
  const totals = rowTotals(row);
  // 마스킹이 앞, 0 접기가 뒤 — 시트 금액 칸과 같은 순서.
  const money = (v: number | null | undefined) => (priceVisible ? formatKRW(v) : '—');
  return [
    String(row.no),
    row.displayName || '(미입력)',
    row.address || row.region || '–',
    String(row.expectedStoreCount ?? row.storeCount),
    row.salesOwnerCode ?? '–',
    row.rentalCompany ?? (row.hasAJ === true ? 'O' : row.hasAJ === false ? 'X' : '–'),
    formatBool(row.hasInsurance),
    robotTypeCell(row).text,
    contractTypeCell(row).text,
    formatRentalTerm(termFacts?.rentalTermMonths),
    String(contractedRobotCount(row) ?? '–'),
    formatDate(row.contract?.contractedAt),
    formatDate(termFacts?.installCompletedAt),
    // 만료는 배지 대신 글자로 — 판정은 시트 배지와 같은 한 벌(isEndDateExpired).
    `${formatDate(termFacts?.endDate)}${isEndDateExpired(row) ? ' (만료)' : ''}`,
    formatDdayOrNull(termFacts?.daysRemaining) ?? '–',
    // 회수일 — 시트 칸과 같은 3형(확정 날짜 / '예정 …' / '–').
    returnDateCellValue(row.returnInfo).text,
    money(totals?.subscriptionFeeKrw),
    money(totals?.salesPriceKrw),
    money(totals?.depositKrw),
    money(totals?.subsidyKrw),
    money(totals?.referralFeeKrw),
    CUSTOMER_MASTER_STATUS_LABEL[row.status],
    // 비고는 시트와 달리 자르지 않는다 — 시트의 30자 자름은 칸 폭 때문이고 파일에는 그
    // 제약이 없다(자르면 조용한 유실).
    row.notes ?? '',
  ];
}

export function buildCustomerMasterCsv(rows: CustomerMasterRow[], priceVisible: boolean): string {
  return [
    customerMasterCsvHeader(priceVisible),
    ...rows.map((r) => customerMasterCsvRow(r, priceVisible)),
  ]
    .map((cells) => cells.map(escapeCsvCell).join(','))
    .join('\n');
}

// round8 (요구 4) — 클릭→필터 셀 버튼 스타일. 활성(현재 필터값)이면 브랜드색
// 강조 + 테두리. 같은 값을 다시 클릭하면 필터 해제(토글)되므로 별도 clear 없이도
// 해제 가능하다.
// round9 (요구 1) — 국내/해외 페이지 내 탭. ServiceOrdersInboxPage 의
// role=tablist + ArrowLeft/Right/Home/End 탐색 패턴을 그대로 따른다.
const SCOPE_TABS = ['domestic', 'overseas'] as const;
type ScopeTab = (typeof SCOPE_TABS)[number];

function ScopeTabButton({
  scopeKey,
  active,
  onActivate,
  children,
}: {
  scopeKey: ScopeTab;
  active: boolean;
  onActivate: () => void;
  children: ReactNode;
}) {
  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>): void {
    const idx = SCOPE_TABS.indexOf(scopeKey);
    let next: ScopeTab | null = null;
    if (e.key === 'ArrowRight') next = SCOPE_TABS[(idx + 1) % SCOPE_TABS.length];
    else if (e.key === 'ArrowLeft')
      next = SCOPE_TABS[(idx - 1 + SCOPE_TABS.length) % SCOPE_TABS.length];
    else if (e.key === 'Home') next = SCOPE_TABS[0];
    else if (e.key === 'End') next = SCOPE_TABS[SCOPE_TABS.length - 1];
    if (!next) return;
    e.preventDefault();
    document.getElementById(`cm-scope-tab-${next}`)?.focus();
  }

  return (
    <button
      type="button"
      role="tab"
      id={`cm-scope-tab-${scopeKey}`}
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      onClick={onActivate}
      onKeyDown={onKeyDown}
      style={{
        padding: '8px 14px',
        border: 'none',
        background: 'transparent',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--fg-brand)' : 'var(--fg-muted)',
        borderBottom: `2px solid ${active ? 'var(--brand)' : 'transparent'}`,
        cursor: 'pointer',
        marginBottom: -1,
      }}
    >
      {children}
    </button>
  );
}

function filterCellStyle(active: boolean): CSSProperties {
  return {
    font: 'inherit',
    color: active ? 'var(--brand)' : 'inherit',
    background: active ? 'var(--bg-sunken)' : 'transparent',
    border: `1px solid ${active ? 'var(--brand)' : 'transparent'}`,
    borderRadius: 6,
    padding: '0 5px',
    cursor: 'pointer',
  };
}

/**
 * 선택 A(26-08-21) → 마감 차수(26-08-24) — 지역/국가 칩이 켜졌을 때 **총계 뒤에 붙는 한
 * 마디의 정본 한 벌.** 머리의 '총 N곳'과 하단 요약('1–N / 총 N건')이 이 함수 하나를 부른다.
 *
 * 왜: 칩은 서버 파라미터가 아니라 **받아온 행 안에서** 화면이 거르는 필터다. 그래서 표에는
 * 20행만 남는데 머리는 '총 121곳', 하단은 '1–121 / 총 121건' 이라고 말했다 — 운영자는
 * 목록이 잘렸다고 읽거나 총계를 지역 숫자로 오해한다. 전체 수도 사실이므로 **지우지 않고**
 * 걸러낸 수를 함께 말한다: 그래야 "왜 20행인가"가 화면에서 설명된다.
 *
 * 단위가 두 자리에서 다르다(머리 '곳' / 하단 '건' — 종전 표기 유지). 문장은 한 벌이고
 * 단위만 인자로 받는다 — 문구를 두 벌 적으면 한쪽만 고쳐지는 날이 온다.
 *
 * @returns 칩이 하나도 없으면 null(붙일 말이 없다 — 소음 금지).
 */
export function chipFilterTotalSuffix(
  chips: { region: string | null; country: string | null },
  filteredCount: number,
  unit: '곳' | '건',
): string | null {
  const parts: string[] = [];
  if (chips.region !== null) parts.push(`지역 '${chips.region}'`);
  if (chips.country !== null) parts.push(`국가 '${chips.country}'`);
  if (parts.length === 0) return null;
  return ` 중 ${parts.join(', ')} ${filteredCount.toLocaleString()}${unit}`;
}

export function CustomerMasterPage({
  scope,
}: {
  // round8 (요구 1) — 국내/해외 분리. 라우트별로 scope 를 주입한다.
  scope?: 'domestic' | 'overseas';
} = {}) {
  const role = useAuthStore((s) => s.user?.role);
  const priceVisible = canSeePrice(role);
  // windows-ux-edit (2026-06, 슬라이드7 / plan §D-7) — 사업팀 write 게이팅을
  // 백엔드 @Roles(ADMIN/MANAGER/SALES_MANAGER) 와 일치시킨다. 기존 canCreate 는
  // SALES_MANAGER 를 누락해 백엔드 정책과 불일치였음(영업팀장이 등록 못 함).
  const canWriteSales = role ? canEditSales(role) : false;
  const canCreate = canWriteSales;
  // 진행 상태 인라인 편집도 동일 권한(ADMIN/MANAGER/SALES_MANAGER).
  const canEditStatus = canWriteSales;
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // FB §10 — 사업 대시보드 카드에서 정렬/필터 프리셋을 URL param 으로 전달.
  //   ?renewal=30|90 → 갱신 임박 필터(expiringWithinDays) 초기값
  //   ?sort=contractDate → 계약일 내림차순 정렬 모드 초기값
  // 마운트 시 1회만 읽어 초기 state 에 반영(이후 사용자가 자유롭게 변경).
  const [searchParams] = useSearchParams();
  const initialRenewal = (() => {
    const raw = searchParams.get('renewal');
    return raw === '30' || raw === '90' ? Number(raw) : undefined;
  })();
  const initialSort: SortMode = (() => {
    const raw = searchParams.get('sort');
    // 26-08-06 — 기본이 '최근 계약일 순'으로 바뀌었다. 대시보드 카드가 ?sort=contractDate 로
    // 켜는 두 모드만 URL 로 받고, 그 외에는 기본 모드로 연다.
    return raw === 'contractDate' || raw === 'contractDateAsc' ? raw : 'latestContract';
  })();
  // 계획서 요구 2b 선택 A·B(26-08-21) — 사업 대시보드 설치 현황이 넘기는 프리셋 셋.
  //   ?region=서울           → 국내 시/도(본사 주소 기준 row.region — 대시보드 지역표의
  //                            고객사 수와 같은 extractRegionKR 정본이라 키가 일치한다.
  //                            단 행의 '대수'는 매장 기준이라 집합이 다를 수 있다 — 그
  //                            이유는 대시보드 표 머리 한 줄이 말한다).
  //   ?region=(지역 미지정)  → 본사 주소가 비어 있는 곳만.
  //   ?country=MN            → 해외 탭의 국가 코드(row.countryCode).
  //   ?former=1              → 기존고객사 필터(서버 former=true — 선택 B 카드).
  //                            마감 차수(26-08-24) 정정: 종전 이 자리에는 "카드 숫자와 같은
  //                            판정·**같은 모수**라 total 이 일치한다"고 적혀 있었는데
  //                            거짓이었다. 판정은 한 벌이지만 이 목록의 모수는 라우트가
  //                            주입하는 scope 로 나라가 갈린다(서버 where countryCode) —
  //                            국가 조건 없는 카드 한 칸과는 해외 기존고객사가 생기는 날
  //                            어긋난다. 그래서 카드가 국내·해외를 나눠 받고, 이 목록
  //                            (scope=domestic)의 total 은 **국내 수**와 일치한다. 해외 탭의
  //                            같은 프리셋(/business/master/overseas?former=1)이 해외 수와
  //                            짝이다 — 카드 부제가 그리로 보낸다.
  // region/country 는 **서버에 파라미터가 없어** 받아온 행 안에서 화면이 거른다(해제 가능한
  // 칩). renewal/sort 와 같은 규칙으로 마운트 시 1회만 읽는다.
  const initialRegion = searchParams.get('region');
  const initialCountry = searchParams.get('country');
  const initialFormer = searchParams.get('former') === '1';

  const [filters, setFilters] = useState<ListCustomerMasterQuery>({
    page: 1,
    // 지역/국가 프리셋으로 열릴 때는 pageSize 를 최대 선택지로 시작한다 — 이 필터는
    // 받아온 행 안에서만 거르므로, 전량(운영 ~121곳 < 200)이 한 페이지에 실려야 결과가
    // 전 범위에 정확하다. 그래도 총계가 페이지를 넘으면 표 위 안내가 그 사실을 말한다
    // (cm-region-partial-notice — 조용한 좁힘 금지).
    pageSize: initialRegion || initialCountry ? Math.max(...PAGE_SIZE_OPTIONS) : PAGE_SIZE,
    expiringWithinDays: initialRenewal,
    scope,
    // 선택 B — 기존고객사 카드가 ?former=1 로 켠다(상태 필터 select 의 '기존고객사' 항목과
    // 같은 서버 파라미터 — 구버전 서버의 400 폴백·안내도 그 경로 그대로 탄다).
    former: initialFormer || undefined,
    // 다계약 표시 차수3(26-08-05) — 정렬 값을 **명시로** 보낸다(SortMode 주석의 근거).
    sort: serverSortFor(initialSort),
  });
  // 선택 A — 지역/국가 클라이언트 필터(해제 가능한 칩). null = 필터 없음.
  const [regionFilter, setRegionFilter] = useState<string | null>(initialRegion);
  const [countryFilter, setCountryFilter] = useState<string | null>(initialCountry);
  // round8 (요구 1) — 국내/해외 탭 전환 시 scope 필터를 갱신(같은 컴포넌트가
  // 두 라우트에서 재사용될 수 있어 마운트 초기값만으론 부족).
  useEffect(() => {
    setFilters((f) => (f.scope === scope ? f : { ...f, scope, page: 1 }));
  }, [scope]);
  // FB §10 — 정렬 모드. 다계약 표시 차수3(26-08-05) — 기본은 '처음 계약일 순'(서버 정렬).
  const [sortMode, setSortMode] = useState<SortMode>(initialSort);
  // PDF §Phase B 페이지 2 §1 — 기본 정렬을 '상태별' 에서 '그룹 없음' 으로
  // 변경. 운영자가 첫 화면에서 sort/필터 적용해 빠르게 훑는 패턴이 더
  // 자연스럽다는 피드백.
  const [grouping, setGrouping] = useState<'NONE' | 'STATUS' | 'SALES' | 'REGION'>('NONE');
  // 머리를 눌러 정렬(2026-10-01) — null 이면 위 정렬 선택(sortMode)이 순서를 정한다.
  const [colSort, setColSort] = useState<ColumnSortState<ColumnSortKey> | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerMasterRow | null>(null);

  // 머리 정렬이 켜지면 한 번에 받아 화면에서 정렬·페이지를 나눈다(COLUMN_SORTS 머리 주석).
  // 페이지를 넘겨도 다시 받지 않도록 page 는 1로 고정한다.
  const listQuery: ListCustomerMasterQuery = colSort
    ? { ...filters, page: 1, pageSize: COLUMN_SORT_FETCH_SIZE }
    : filters;
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['customer-master', listQuery],
    queryFn: () => customerMasterApi.list(listQuery),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  // PDF §Phase B 페이지 2 §3 — 진행 상태 인라인 편집 mutation.
  // 성공 시 list query invalidate + toast. 실패 시 즉시 에러 toast (이전
  // optimistic update 안 함 — list 가 status 합성이라 backend round-trip
  // 후 정확한 값을 받는 게 안전).
  // updatingRowId — 현재 mutation 중인 row ID 만 busy 처리.
  // 전체 rows 를 block 하지 않도록 per-row 로 범위를 좁힘.
  const [updatingRowId, setUpdatingRowId] = useState<string | null>(null);
  // FB §4b — '회수 예정' 선택 시 사유 입력 모달. status 변경을 즉시 mutate
  // 하지 않고 이 prompt 를 띄워 사유를 받은 뒤 함께 전송한다.
  const [returnReasonFor, setReturnReasonFor] = useState<{ id: string; name: string } | null>(null);
  const updateStatusMx = useMutation({
    mutationFn: ({
      id,
      status,
      reason,
    }: {
      id: string;
      status: CustomerMasterStatus | null;
      reason?: string;
    }) => {
      setUpdatingRowId(id);
      return customerMasterApi.updateStatus(id, status, reason);
    },
    onSuccess: () => {
      // S1 Phase 11B (R2 #1) — customer-master 만 invalidate 하면 계약/인프라/
      // 배정/대시보드 등 다른 팀 화면이 stale 캐시로 남아 상태 변화가 반영되지
      // 않는다. cascade 헬퍼로 연관 도메인을 일괄 무효화.
      invalidateCustomerMasterCascade(qc);
      toast.success('진행 상태가 업데이트되었습니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '상태 업데이트 실패'),
    onSettled: () => setUpdatingRowId(null),
  });

  // 이슈1e — 삭제된 고객사 복구/완전삭제(ADMIN). 백엔드는 이미 customers
  // softDelete/restore + admin purge(cascade) 를 제공하므로 그 엔드포인트를
  // 그대로 재사용한다(라우팅이 끊긴 CustomersPage 에만 있던 UI 를 정규 탭으로).
  const isAdmin = role === UserRole.ADMIN;
  const showActions = isAdmin && !!filters.includeDeleted;
  // round9 (요구 8) — 관리 열을 삭제행 복구/완전삭제(showActions) 전용에서
  // 활성 행 편집 연필까지 포함하도록 확장. canWriteSales 사용자는 삭제됨
  // 포함 모드가 아니어도 열이 보여야 하므로 별도 플래그로 분리.
  const showActionsColumn = canWriteSales || showActions;

  // 이슈1d — 고객 유형 필터를 customer.segment OptionGroup 에서 가져와 신규 등록
  // 모달(EditableSelect)의 편집과 동기화(기존엔 정적 SEGMENT_LABEL enum 만 읽어
  // 운영자가 추가/보관한 값이 필터에 안 떴다).
  const segmentValuesQ = useQuery({
    queryKey: ['option-groups', 'customer.segment'],
    queryFn: () => optionGroupsApi.listValues('customer.segment'),
    staleTime: 5 * 60_000,
  });

  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [purgeFor, setPurgeFor] = useState<CustomerMasterRow | null>(null);
  const restoreMx = useMutation({
    mutationFn: (id: string) => {
      setActionBusyId(id);
      return customersApi.restore(id);
    },
    onSuccess: () => {
      invalidateCustomerMasterCascade(qc);
      toast.success('고객사를 복구했습니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '복구 실패'),
    onSettled: () => setActionBusyId(null),
  });
  const purgeMx = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => {
      setActionBusyId(id);
      return customersApi.purge(id, name);
    },
    onSuccess: () => {
      invalidateCustomerMasterCascade(qc);
      setPurgeFor(null);
      toast.success('고객사를 완전 삭제했습니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '완전 삭제 실패'),
    onSettled: () => setActionBusyId(null),
  });

  // FB §4b — status 변경 진입점. '회수 예정'이면 사유 모달을 먼저 띄우고,
  // 그 외 상태는 즉시 mutate. (StatusEditCell 의 onChange 가 이 함수를 호출.)
  function handleStatusChange(id: string, status: CustomerMasterStatus | null): void {
    if (status === 'return_planned') {
      const row = (data?.items ?? []).find((r) => r.id === id);
      setReturnReasonFor({ id, name: row?.displayName ?? '' });
      return;
    }
    updateStatusMx.mutate({ id, status });
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  // 정정5(26-08-06) — 이 응답이 **정렬을 빼고** 받아온 것인가(구버전 api 가 400 으로 거절).
  // 값이 있으면 화면이 고른 순서가 아니므로 정렬 기준 문구와 표 위 안내가 그 사실을 말한다.
  const rejectedSort = data?.rejectedSort ?? null;
  // 계획서 요구 2a §3·§4(26-08-21) — 기존고객사 필터가 켜져 있는가. '종료 포함' 토글의
  // 자동 켜짐+잠김과 빈 상태 문구가 이 값으로 갈린다.
  const formerActive = !!filters.former;
  // 이 응답이 **기존고객사 필터를 빼고** 받아온 것인가(구버전 api 가 400 으로 거절).
  // true 면 지금 목록은 기존고객사만이 아니다 — 표 위 안내가 그 사실을 말한다.
  const rejectedFormer = data?.rejectedFormer ?? false;
  // 계획서 요구 2a §2(26-08-21) — 회수일(returnInfo)을 아직 내리지 않는 서버의 응답인가.
  // 그 행들의 회수일 칸은 '–' 인데, 이유를 말하지 않으면 '회수 기록이 없다'로 읽힌다
  // (조용한 빈칸 금지 — 화면 규칙 절의 구버전 관례).
  const returnInfoMissingCount = items.filter((r) => r.returnInfo === undefined).length;

  // 개선 9건 차수2f(26-08-04) — 합계를 받아야 하는데 못 받은 행이 몇 곳인가.
  // 이 화면은 그 경우 금액 5칸·계약 대수를 비운다(옛 계산으로 되돌리지 않는다) —
  // 비운 이유를 말하지 않으면 그것이 '조용한 유실'이 된다. 정상 상태에서는 0 이라
  // 이 안내가 아예 없다(계약이 없는 고객사는 세지 않는다 — totalsMissing 주석).
  const totalsMissingCount = items.filter(totalsMissing).length;

  // 계획서 요구 2b 선택 A(26-08-21) — 지역/국가 클라이언트 필터. 서버에 파라미터가 없어
  // 받아온 행 안에서 거른다(프리셋 진입 시 pageSize 최대로 전량이 실린다 — filters 초기값
  // 주석). 기준은 대시보드 지역표의 고객사 수와 같다: region 은 본사 주소(row.region =
  // 서버 extractRegionKR 정본), country 는 국가 코드. 어느 쪽도 안 켜져 있으면 원본 그대로.
  const clientFilteredItems = useMemo(() => {
    let rows = items;
    if (regionFilter !== null) rows = rows.filter((r) => r.region === regionFilter);
    if (countryFilter !== null) rows = rows.filter((r) => r.countryCode === countryFilter);
    return rows;
  }, [items, regionFilter, countryFilter]);

  // 마감 차수(26-08-24) — 칩이 켜져 있으면 **머리와 하단 요약이 걸러낸 수를 말한다**
  // (정본 한 벌 chipFilterTotalSuffix — 그 함수 주석에 근거). 단위만 다르다.
  const chipSuffixForHeader = chipFilterTotalSuffix(
    { region: regionFilter, country: countryFilter },
    clientFilteredItems.length,
    '곳',
  );
  const chipSuffixForPager = chipFilterTotalSuffix(
    { region: regionFilter, country: countryFilter },
    clientFilteredItems.length,
    '건',
  );

  // FB §10 — 정렬 모드 적용. 시트 계약일 2종만 화면이 재정렬한다(원본 불변 유지를 위해
  // 복사 후 sort). 나머지는 서버가 정렬한 순서 그대로다 — 어느 쪽인지는 SortMode 주석에 있고,
  // 사용자에게는 정렬 선택지의 **라벨**이 말한다(26-08-06 부터 옆의 기준 문구는 없다).
  // 그룹화/ungrouped 양쪽 렌더가 이 결과를 사용한다.
  const modeSortedItems = useMemo(() => {
    // 서버가 정렬해 주는 모드는 응답 순서를 그대로 쓴다(처음 계약일 순 · 등록 최근순 · 가나다순).
    if (sortMode !== 'contractDate' && sortMode !== 'contractDateAsc') return clientFilteredItems;
    const direction = sortMode === 'contractDateAsc' ? 'asc' : 'desc';
    return [...clientFilteredItems].sort((a, b) => compareByContractDate(a, b, direction));
  }, [clientFilteredItems, sortMode]);

  // 머리 정렬(2026-10-01) — 받아 온 전부를 그 칸 기준으로 줄 세운 뒤 지금 페이지만 자른다.
  // 같은 값끼리는 위 정렬 선택의 순서를 물려받는다(안정 정렬). No. 는 화면 순서대로 다시 매긴다
  // (서버가 매긴 번호는 서버 순서라 머리 정렬 뒤에는 뒤섞여 보인다).
  const colSortedAll = useMemo(() => {
    if (!colSort) return null;
    const spec = COLUMN_SORTS[colSort.key];
    return sortRowsByColumn(modeSortedItems, spec.kind, spec.value, colSort.step);
  }, [modeSortedItems, colSort]);
  const pageNo = filters.page ?? 1;
  const pageSizeNow = filters.pageSize ?? PAGE_SIZE;
  const sortedItems = useMemo(() => {
    if (!colSortedAll) return modeSortedItems;
    const start = (pageNo - 1) * pageSizeNow;
    return colSortedAll
      .slice(start, start + pageSizeNow)
      .map((r, i) => ({ ...r, no: start + i + 1 }));
  }, [colSortedAll, modeSortedItems, pageNo, pageSizeNow]);
  // 머리 정렬은 받아 온 행 안에서만 줄 세운다 — 상한을 넘는 고객사가 있으면 그 사실을 말한다.
  const colSortPartial = !!colSort && total > items.length;
  function toggleColumnSort(key: ColumnSortKey): void {
    setColSort((cur) => nextColumnSort(cur, key, COLUMN_SORTS[key].kind));
    setFilters((f) => ({ ...f, page: 1 }));
  }
  const sortableTh = (key: ColumnSortKey, label: ReactNode) => (
    <SortableTh
      label={label}
      kind={COLUMN_SORTS[key].kind}
      step={colSort?.key === key ? colSort.step : null}
      disabled={!!COLUMN_SORTS[key].price && !priceVisible}
      onToggle={() => toggleColumnSort(key)}
      testId={`cm-sort-th-${key}`}
      thStyle={th}
    />
  );

  // Group rows
  const groups = useMemo(() => {
    if (grouping === 'NONE') return null;
    const keyFn =
      grouping === 'STATUS'
        ? (r: CustomerMasterRow) => CUSTOMER_MASTER_STATUS_LABEL[r.status]
        : grouping === 'SALES'
          ? (r: CustomerMasterRow) => r.salesOwnerCode || '(영업 미지정)'
          : (r: CustomerMasterRow) => r.region;

    const buckets = new Map<string, CustomerMasterRow[]>();
    // sortedItems 를 순회 → 그룹 내부 행 순서도 정렬 모드를 따른다.
    for (const r of sortedItems) {
      const k = keyFn(r);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k)!.push(r);
    }

    // status 그룹화 시 STATUS_ORDER 우선; 다른 그룹화는 데이터 많은 순
    const sortedKeys = Array.from(buckets.keys()).sort((a, b) => {
      if (grouping === 'STATUS') {
        const labelOrder = STATUS_ORDER.map((s) => CUSTOMER_MASTER_STATUS_LABEL[s]);
        return labelOrder.indexOf(a) - labelOrder.indexOf(b);
      }
      return (buckets.get(b)?.length ?? 0) - (buckets.get(a)?.length ?? 0);
    });

    return sortedKeys.map((label) => {
      const rows = buckets.get(label)!;
      // 개선 9건 차수2f(26-08-04) — 그룹 머리의 두 값도 **합계 자리**를 읽는다
      // (contractedRobotCount / rowTotals 한 벌 — 시트 칸과 같은 값이라 이 합은 그 칸들의
      // 합이다). 종전엔 여기서 최신 계약 1건을 더하거나(구독 1대 + 판매 1대인 고객사가
      // '1대') 계약 상태를 보지 않고 전부 더했다(만료 계약 금액이 누적에 들어갔다).
      // 합계를 못 받은 행은 더할 값이 없어 빠진다 — 그 사실은 표 위 안내가 말한다.
      const contractedSum = rows.reduce((s, r) => s + (contractedRobotCount(r) ?? 0), 0);
      const deployedSum = rows.reduce((s, r) => s + r.deployedRobotCount, 0);
      // 이슈1c — 그룹 '누적'은 고객사별 월 구독료 합계의 합이다(첫 라인 대당가 × 전체
      // 수량이던 종전 근사가 멀티-모델 고객에서 부정확했다).
      const priceSum = priceVisible
        ? rows.reduce((s, r) => s + (rowTotals(r)?.subscriptionFeeKrw ?? 0), 0)
        : 0;
      // 두 대수의 뜻이 갈리므로 **머리 단어로 구분한다**: '계약'은 계약서에 적힌 대수(전
      // 계약 합 = 시트 '계약 대수' 칸의 합), '배치'는 실제로 나가 있는 로봇 수(고객사 전체
      // — 배치 행에는 계약 칸이 없어 계약별로 쪼갤 수 없다). 종전 '로봇 N대'는 어느
      // 쪽인지 말하지 않아 '배치 N대'와 같은 뜻으로 읽혔다.
      // 앞의 건수도 '곳'으로 적는다 — '건'은 이제 계약 건수로 읽힌다('계약 2건' 배지).
      const meta = [
        `고객사 ${rows.length}곳`,
        `계약 ${contractedSum}대`,
        `배치 ${deployedSum}대`,
        priceVisible ? `구독료 누적 ${formatKRW(priceSum)}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
      return { label, rows, meta };
    });
  }, [sortedItems, grouping, priceVisible]);

  // 계획서 §7ⓑ(26-08-21) — CSV 내보내기. 지금 보이는 행(현재 페이지·필터·정렬)을 시트와
  // 같은 표기 정본으로 내려받는다. 내려받기 절차는 리포의 다른 CSV 내보내기와 같은 관례
  // (BOM + blob + KST 파일명 — BusinessDashboardPage handleExportExpiringContracts).
  function handleExportCsv(): void {
    if (sortedItems.length === 0) return;
    const csv = buildCustomerMasterCsv(sortedItems, priceVisible);
    // 엑셀 UTF-8 호환 BOM(리터럴 U+FEFF — 리포의 다른 CSV 내보내기 3곳과 같은 표기).
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-master-${kstTodayYmd()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {/* round9 (요구 1) — 사이드바 국내/해외 2항목 진입을 페이지 내 탭으로 이동.
            2026-10-01 오너 요청 — 상단 바(라우트 제목)와 자리를 바꿨다: 상단 바는 '고객사 마스터',
            이 큰 제목이 '고객사 마스터 국내/해외'를 말한다. */}
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          고객사 마스터
          {scope === 'domestic' ? ' 국내' : scope === 'overseas' ? ' 해외' : ''}
        </h1>
        {/* 마감 차수(26-08-24) — 칩이 켜져 있으면 걸러낸 수를 함께 말한다(전체 수는 유지 —
            둘 다 보여야 "왜 표에 20행뿐인가"가 설명된다). 문구는 정본 한 벌. */}
        <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
          {isFetching
            ? '불러오는 중…'
            : `총 ${total.toLocaleString()}곳${chipSuffixForHeader ?? ''}`}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {canCreate && (
            <Button type="button" onClick={() => setCreateOpen(true)}>
              + 신규 등록
            </Button>
          )}
        </div>
      </div>

      {/* round9 (요구 1) — 국내/해외 페이지 내 탭. 두 서브라우트(/business/master,
          /business/master/overseas)는 그대로 두고(딥링크·TopBar handle.title 보존),
          탭 클릭이 그 사이를 navigate(replace) 한다. */}
      <div
        role="tablist"
        aria-label="고객사 마스터 국내/해외"
        style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-subtle)' }}
      >
        <ScopeTabButton
          scopeKey="domestic"
          active={scope !== 'overseas'}
          onActivate={() => navigate('/business/master', { replace: true })}
        >
          국내
        </ScopeTabButton>
        <ScopeTabButton
          scopeKey="overseas"
          active={scope === 'overseas'}
          onActivate={() => navigate('/business/master/overseas', { replace: true })}
        >
          해외
        </ScopeTabButton>
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: '8px 12px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
        }}
      >
        <input
          placeholder="상호, 위치, 담당자 검색"
          value={filters.search ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, search: e.target.value || undefined, page: 1 }))
          }
          style={toolInput}
        />
        <select
          value={formerActive ? FORMER_FILTER_VALUE : (filters.status ?? '')}
          onChange={(e) => {
            const v = e.target.value;
            // 계획서 요구 2a §3·§4(26-08-21) — 기존고객사는 상태 enum 이 아니라 **별도
            // 파라미터**(파생 분류)다. 손잡이는 화면 규칙대로 이 상태 필터의 항목이지만,
            // 서버로는 status 가 아니라 former=true 로 간다.
            if (v === FORMER_FILTER_VALUE) {
              setFilters((f) => ({ ...f, status: undefined, former: true, page: 1 }));
              return;
            }
            setFilters((f) => ({
              ...f,
              status: (v || undefined) as CustomerMasterStatus | undefined,
              former: undefined,
              page: 1,
            }));
          }}
          style={toolSelect}
          title="진행 상태"
        >
          <option value="">전체 상태</option>
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {CUSTOMER_MASTER_STATUS_LABEL[s]}
            </option>
          ))}
          {/* 화면 규칙(26-08-21) — 새 UI 를 만들지 않고 기존 상태 필터에 항목으로.
              라벨이 판정 조건까지 말한다(계약·로봇 없음). */}
          <option value={FORMER_FILTER_VALUE}>기존고객사 (계약과 로봇 없음)</option>
        </select>
        {/* 이슈1d — 고객 유형 필터를 customer.segment OptionGroup 에서 채운다.
            모달(EditableSelect)이 같은 그룹을 쓰므로 add/archive 가 즉시 반영된다.
            라벨은 OptionValue.label > 정적 SEGMENT_LABEL(레거시 enum 코드) > 원시값 순. */}
        <select
          value={filters.segment ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, segment: e.target.value || undefined, page: 1 }))
          }
          style={toolSelect}
          title="고객 유형"
        >
          <option value="">고객 유형 전체</option>
          {(segmentValuesQ.data ?? []).map((opt) => (
            <option key={opt.id} value={opt.value}>
              {opt.label ?? SEGMENT_LABEL[opt.value as CustomerSegment] ?? opt.value}
            </option>
          ))}
        </select>
        <select
          value={grouping}
          onChange={(e) => setGrouping(e.target.value as typeof grouping)}
          style={toolSelect}
          title="그룹화"
        >
          <option value="NONE">그룹 없음</option>
          <option value="STATUS">상태별</option>
          <option value="SALES">영업 담당별</option>
          <option value="REGION">지역별</option>
        </select>
        {/* FB §10 — 정렬 select. 사업 대시보드 "이번 달 신규 계약" 카드가
            ?sort=contractDate 로 시트 계약일 내림차순을 프리셋한다.
            다계약 표시 차수3(26-08-05) — 기본이 '처음 계약일 순'(서버 정렬)으로 바뀌었다.
            기본값이 바뀌었으므로 **무엇을 기준으로 정렬 중인지** 옆에 적는다 — 계약이 여럿인
            고객사에서 '처음 계약일'과 시트의 '계약일' 칸(최신 계약)이 다른 날이기 때문이다. */}
        <select
          value={sortMode}
          onChange={(e) => {
            const next = e.target.value as SortMode;
            setSortMode(next);
            // 정렬을 새로 고르면 머리 정렬은 푼다 — 두 순서가 겹치면 무엇이 이기는지 화면이 말해야 한다.
            setColSort(null);
            setFilters((f) => ({ ...f, sort: serverSortFor(next), page: 1 }));
          }}
          style={toolSelect}
          title="정렬"
        >
          {SORT_OPTIONS.map((mode) => (
            <option key={mode} value={mode}>
              {SORT_LABEL[mode]}
            </option>
          ))}
        </select>
        {/* 26-08-06 오너 요구 — 정렬 옆의 **평소 기준 설명은 없앴다.**
            거절 안내만 남긴다: 고른 정렬이 먹지 않은 때(구버전 api) 침묵하면 화면이 순서를 두고
            거짓말을 하기 때문이다. 평소에는 이 자리에 아무것도 그리지 않는다(빈 span 도 남기지
            않는다 — 도구줄 간격이 벌어진다). */}
        {rejectedSort && (
          <span
            data-testid="cm-sort-basis"
            className="kuun-caption"
            style={{ color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}
          >
            {SORT_REJECTED_BASIS_TEXT}
          </span>
        )}
        <select
          value={filters.expiringWithinDays ?? ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              expiringWithinDays: e.target.value ? Number(e.target.value) : undefined,
              page: 1,
            }))
          }
          style={toolSelect}
          title="갱신 임박 필터"
        >
          <option value="">갱신 필터 없음</option>
          <option value="30">30일 내 갱신</option>
          <option value="90">90일 내 갱신</option>
        </select>
        {/* 이슈1e — ADMIN 전용 '삭제됨 포함' 토글. 켜면 삭제된 고객사가 행으로
            노출되고 '관리' 열에 복구/완전삭제 액션이 나타난다. */}
        {isAdmin && (
          <label style={includeDeletedLabel}>
            <input
              type="checkbox"
              checked={!!filters.includeDeleted}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  includeDeleted: e.target.checked || undefined,
                  page: 1,
                }))
              }
            />
            삭제됨 포함
          </label>
        )}
        {/* round10 (요구 5) — '종료'(retired) 고객사 기본 숨김. 전 사용자 노출.
            계획서 요구 2a §3(26-08-21) — 기존고객사 필터가 켜져 있으면 **자동 켜짐+잠김**
            (화면 규칙: 손잡이 두 개가 서로 싸우지 않게 — 기존고객사는 '종료'로 계산되는
            고객사들이라 이 토글이 꺼진 채면 필터가 항상 빈 목록이 된다). 서버는 former=true
            만으로 종료 숨김을 스스로 이기므로 filters.includeRetired 값은 **건드리지 않는다**
            — 필터를 끄면 운영자의 종전 선택이 그대로 돌아온다. */}
        <label
          style={
            formerActive ? { ...includeDeletedLabel, cursor: 'not-allowed' } : includeDeletedLabel
          }
          title={
            formerActive
              ? "기존고객사 필터가 켜져 있어 '종료 포함'도 함께 켜집니다. 기존고객사는 '종료'로 계산되는 고객사라서 끌 수 없습니다."
              : undefined
          }
        >
          <input
            type="checkbox"
            checked={formerActive || !!filters.includeRetired}
            disabled={formerActive}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                includeRetired: e.target.checked || undefined,
                page: 1,
              }))
            }
          />
          종료 포함
        </label>
        {/* 계획서 요구 2b 선택 A(26-08-21) — 대시보드 지역표 행 클릭이 켠 지역/국가 필터.
            서버 파라미터가 아니라 화면 필터라서, 켜져 있다는 사실을 **칩으로 보이게** 하고
            ✕ 로 해제한다(보이지 않는 필터는 조용한 좁힘이다). */}
        {regionFilter !== null && (
          <span data-testid="cm-region-chip" style={presetChip}>
            지역: {regionFilter}
            <button
              type="button"
              aria-label="지역 필터 해제"
              onClick={() => setRegionFilter(null)}
              style={presetChipClear}
            >
              ✕
            </button>
          </span>
        )}
        {countryFilter !== null && (
          <span data-testid="cm-country-chip" style={presetChip}>
            국가: {countryFilter}
            <button
              type="button"
              aria-label="국가 필터 해제"
              onClick={() => setCountryFilter(null)}
              style={presetChipClear}
            >
              ✕
            </button>
          </span>
        )}
        {/* 계획서 §7ⓑ(26-08-21) — CSV 내보내기. 지금 보이는 행(현재 페이지·필터·정렬)을
            시트 칸과 같은 표기 정본으로 내려받는다(buildCustomerMasterCsv 머리 주석).
            9/2 오너 요청 — 줄의 **오른쪽 끝**으로(marginLeft:auto). 필터 손잡이들 사이에
            섞여 있으면 버튼이 필터처럼 보인다. 왼쪽은 '고르는 것', 오른쪽은 '하는 것' —
            로봇 목록이 이미 쓰는 규칙이라 두 화면이 같은 어휘가 된다(RobotsListPage).
            수제 스타일 대신 공용 보조 버튼을 쓴다(같은 행동이 화면마다 다르게 생기지 않게). */}
        <Button
          type="button"
          variant="secondary"
          onClick={handleExportCsv}
          disabled={sortedItems.length === 0}
          title="지금 화면에 보이는 행을 페이지, 필터, 정렬 그대로 CSV로 내보냅니다"
          // 줄이 접히면 마지막 줄의 오른쪽 끝에 붙는다(계획서에 적힌 그대로) —
          // 도구줄이 flex-wrap 이라 auto 여백은 자기 줄 안에서 민다.
          style={{ marginLeft: 'auto' }}
        >
          CSV 내보내기
        </Button>
      </div>

      {/* 선택 A(26-08-21) — 지역/국가 필터는 받아온 행 안에서만 거른다. 전량이 한 페이지에
          안 실린 때(총계 > 받아온 수) 침묵하면 다른 페이지의 해당 지역 고객사가 조용히
          빠진 결과를 전부인 줄 알게 된다 — 그 사실을 표 위에서 한 번 말한다. */}
      {(regionFilter !== null || countryFilter !== null) && total > items.length && (
        <div role="status" data-testid="cm-region-partial-notice" style={totalsMissingNotice}>
          지역과 국가 필터는 지금 받아온 {items.length}곳 안에서만 거릅니다. 전체{' '}
          {total.toLocaleString()}곳 중 나머지는 페이지를 넘겨 확인해 주세요.
        </div>
      )}

      {/* 다계약 표시 차수3 정정5(26-08-06) — 이 서버가 정렬 값을 거절해 **정렬 없이** 받아온
          응답. 종전 코드는 이 400 을 그대로 useQuery 에 흘려 시트가 통째로 비었다(콘솔이 먼저
          배포되는 구간의 전 사용자 · 전 페이지). 이제 시트는 뜨고, **순서가 화면이 고른 것이
          아니라는 사실**을 여기서 한 번 말한다 — 정렬 select 는 여전히 고른 값을 보여 주므로
          이 문장이 없으면 화면이 거짓말한다. api 를 먼저 배포하는 것이 정답이고 그 순서는
          배포 런북에 적혀 있다(docs/work_log/resume-interrupted-deploy-2026-07-29). */}
      {colSortPartial && (
        <div role="status" data-testid="cm-colsort-partial-notice" style={totalsMissingNotice}>
          칸 머리로 정렬하면 한 번에 받을 수 있는 {COLUMN_SORT_FETCH_SIZE}곳 안에서만 정렬합니다.
          전체 {total.toLocaleString()}곳 중 {items.length}곳만 정렬됐습니다. 검색이나 필터로 좁히면
          전부 정렬됩니다.
        </div>
      )}
      {rejectedSort && (
        <div role="status" data-testid="cm-sort-rejected-notice" style={totalsMissingNotice}>
          서버가 아직 &lsquo;{SORT_LABEL[sortMode]}&rsquo; 정렬을 지원하지 않습니다. 목록이 비지
          않도록 <strong>정렬 없이 다시 받아</strong> 서버 기본 순서로 보여 주고 있습니다. 지금 목록
          순서는 위에서 고른 순서가 아닙니다. 서버가 업데이트되면 고른 정렬로 돌아옵니다. 행, 금액,
          상태 값은 정상입니다.
        </div>
      )}

      {/* 계획서 요구 2a §4(26-08-21) — 이 서버가 기존고객사 필터를 거절해 **필터 없이**
          받아온 응답(구버전 api). 걸러진 척 침묵하면 운영자가 전 고객사를 기존고객사로
          읽는다 — 정렬 거절 안내와 같은 규칙으로 그 사실을 한 번 말한다. */}
      {rejectedFormer && (
        <div role="status" data-testid="cm-former-rejected-notice" style={totalsMissingNotice}>
          서버가 아직 기존고객사 필터를 지원하지 않습니다. 목록이 비지 않도록{' '}
          <strong>필터 없이 다시 받아</strong> 보여 주고 있습니다. 지금 목록은 기존고객사만이
          아닙니다. 서버가 업데이트되면 고른 필터로 돌아옵니다.
        </div>
      )}

      {/* 계획서 요구 2a §2(26-08-21) — 회수일(returnInfo)을 아직 내리지 않는 서버(구버전).
          회수일 칸이 비는 이유를 표 단위로 한 번 말한다(조용한 빈칸 금지 — 화면 규칙 절). */}
      {returnInfoMissingCount > 0 && (
        <div role="status" data-testid="cm-return-info-missing-notice" style={totalsMissingNotice}>
          서버가 아직 회수일을 보내 주지 않아 {returnInfoMissingCount}곳의 회수일 칸을 비워
          두었습니다. 서버가 업데이트되면 채워집니다. 다른 칸의 값은 정상입니다.
        </div>
      )}

      {/* 개선 9건 차수2f(26-08-04) — 합계를 못 받은 행이 있으면 표 위에서 한 번 말한다.
          행마다 배지가 붙기도 하지만(그 행의 '합계 없음'), 그룹 머리의 '계약 N대'·'구독료
          누적'도 함께 낮아지므로 그 사실은 표 단위로 알려야 한다. */}
      {totalsMissingCount > 0 && (
        <div role="status" data-testid="cm-totals-missing-notice" style={totalsMissingNotice}>
          금액과 계약 대수 합계를 아직 받지 못한 고객사가 {totalsMissingCount}곳입니다. 그 행의 금액
          다섯 칸과 계약 대수는 비워 두었고, 그룹 머리의 계약 대수와 구독료 누적에도 더하지
          않았습니다. 진행 중인 계약만 세는 새 기준으로만 채우며, 해지되거나 만료된 계약까지 더하던
          예전 계산으로는 되돌리지 않습니다.
        </div>
      )}

      {/* Sheet */}
      <div style={{ flex: 1, minHeight: 400, overflow: 'hidden' }}>
        <div
          style={{
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-card)',
            background: 'var(--bg-surface)',
            overflow: 'auto',
            height: '100%',
          }}
        >
          {isLoading ? (
            <div style={emptyCell}>불러오는 중…</div>
          ) : error ? (
            <div style={{ ...emptyCell, color: 'var(--fg-danger)' }}>{extractApiError(error)}</div>
          ) : sortedItems.length === 0 ? (
            <div style={emptyCell}>
              {/* 계획서 요구 2a §3(26-08-21) — 기존고객사 필터의 빈 상태는 화면 규칙의
                  문장 그대로(어떤 곳이 여기 오는지 정의를 말한다). 단, 구버전 서버가
                  필터를 거절해 필터 없이 받아온 빈 목록(rejectedFormer)은 이 문장을 쓰면
                  거짓이다 — 그 목록은 기존고객사로 걸러진 결과가 아니다.
                  선택 A(26-08-21) — 지역/국가 칩이 목록을 0으로 만든 때는 **그 필터**가
                  이유라고 말한다(칩을 지우면 돌아온다) — 서버 결과가 0인 것과 다른 사실이다. */}
              {items.length > 0 && (regionFilter !== null || countryFilter !== null)
                ? `'${regionFilter ?? countryFilter}'에 해당하는 고객사가 받아온 목록에 없습니다. 위 칩의 ✕를 누르면 전체가 다시 보입니다.`
                : formerActive && !rejectedFormer
                  ? '기존고객사가 없습니다. 계약이 끝나고 로봇이 모두 회수된 곳이 여기에 나옵니다.'
                  : '조건에 맞는 고객사가 없습니다.'}
            </div>
          ) : (
            <table style={table}>
              <thead style={thead}>
                <tr>
                  {/*
                   * 수정 사항2.pdf p.2 #1 — 컬럼 ↔ 데이터 불일치 수정.
                   * 이전 PDF §Phase B 페이지 2 §4 에서 body 의 담당자/직함/
                   * 연락처 cell 은 detail 페이지로 이관하면서 제거됐는데,
                   * head 의 같은 3개 <th> 가 그대로 남아 모든 row 가
                   * 3칸씩 좌측으로 밀려 보였음 (예: '0' 이 담당자 컬럼
                   * 아래에 표시됨). head 도 함께 제거하여 정합 회복.
                   * StatusGroupHeader 의 colSpan 도 동기 (23 → 20).
                   */}
                  <th style={th}>No.</th>
                  {sortableTh('name', '상호')}
                  {sortableTh('location', '위치')}
                  {sortableTh('storeCount', '매장 수')}
                  {sortableTh('salesOwner', '영업 담당')}
                  {sortableTh('rentalCompany', '렌탈사')}
                  {sortableTh('insurance', '보증 보험')}
                  {/* round5 #22 — '희망 도입일' 컬럼 제거(확정: 시트에서 완전 제거).
                      대신 실제 '도입일'(설치 완료)을 계약일↔반납일 사이에 신설.
                      희망 도입일 입력값(Deal.expectedCloseAt)은 인프라 발주
                      모달의 참고 표기로 이동. */}
                  {sortableTh('robotType', COLUMN_LABEL.robotType)}
                  {/* 계약 사실화 1단계 정정6(26-07-29) — '계약 형태'. 계획 §1 이 전제한
                      데이터 정리 스윕의 실제 동선(모델 바로 옆에 두어 "GEN2 / 구독"이
                      한눈에 대조된다). 서버는 이 값을 이미 내려주고 있었고 읽는 화면이
                      없었다. 이 표는 폭을 고정하지 않고(table width:100% + nowrap +
                      컨테이너 overflow:auto — 파일 머리 주석 "35+ 컬럼을 가로 스크롤로
                      표시") 칸이 늘면 가로로 넓어지므로, 다른 칸이 좁아지지 않는다. */}
                  {/* 개선 9건 차수2e(26-08-04) ⑤ — 열 이름은 COLUMN_LABEL 정본 한 벌에서
                      온다. 다계약 배지의 안내문이 이 이름들을 그대로 부르므로(어느 칸이
                      어느 계약 기준인지 말한다) 두 곳에 따로 적으면 갈라진다. */}
                  <th style={th}>{COLUMN_LABEL.contractType}</th>
                  {sortableTh('rentalTerm', COLUMN_LABEL.rentalTerm)}
                  {sortableTh('contractQty', COLUMN_LABEL.contractQty)}
                  {sortableTh('contractedAt', COLUMN_LABEL.contractedAt)}
                  {sortableTh('installCompletedAt', COLUMN_LABEL.installCompletedAt)}
                  {sortableTh('endDate', COLUMN_LABEL.endDate)}
                  {sortableTh('dday', COLUMN_LABEL.dday)}
                  {/* 계획서 요구 2a §2(26-08-21) — 회수일. 반납일·D-Day 바로 옆에 두어 계약
                      수명주기(도입 → 반납 예정 → 회수)가 한 묶음으로 읽힌다(화면 규칙 절 —
                      시트가 24열이 되지만 이 묶음 배치가 가로 스크롤 부담을 상쇄한다). */}
                  {sortableTh('returnedAt', COLUMN_LABEL.returnedAt)}
                  {/* 사용자 요청 — 목록은 per-unit 구독료/판매가 대신 전 라인 합산
                      '총 구독료'·'총 판매가' 두 총계만 노출(모델별 내역은 상세 페이지
                      계약 카드에서 확인). 보증금은 별도 항목이라 유지. */}
                  {sortableTh(
                    'subscriptionFee',
                    lockableColumn(COLUMN_LABEL.totalSubscriptionFee, priceVisible),
                  )}
                  {sortableTh(
                    'salesPrice',
                    lockableColumn(COLUMN_LABEL.totalSalesPrice, priceVisible),
                  )}
                  {sortableTh('deposit', lockableColumn(COLUMN_LABEL.deposit, priceVisible))}
                  {/* 개선 9건 차수2(26-08-04) §7 (결정 ④A) — 보증금 **옆에** 지원금·소개비.
                      폭 배분 확인 결과: 이 표는 폭을 고정하지 않는다(table width:100% +
                      td nowrap + 컨테이너 overflow:auto — 파일 머리 주석 "35+ 컬럼을 가로
                      스크롤로 표시"). 칸이 늘면 표가 가로로 넓어지고 다른 칸은 좁아지지
                      않는다 — 정정6 이 '계약 형태' 칸을 넣을 때 확인해 둔 것과 같은 근거다.
                      금액 3칸이 나란히 붙으므로 잠금 표기도 보증금과 같은 모양으로 맞춘다. */}
                  {sortableTh('subsidy', lockableColumn(COLUMN_LABEL.subsidy, priceVisible))}
                  {sortableTh(
                    'referralFee',
                    lockableColumn(COLUMN_LABEL.referralFee, priceVisible),
                  )}
                  {sortableTh('status', COLUMN_LABEL.status)}
                  {/* 운영자 피드백 — '방향' 컬럼 제거. 로봇 1대의 단일
                      direction 값이 고객사 단위 시트에는 의미가 약함
                      (멀티-로봇 고객사에서 첫 번째 row 만 노출). */}
                  <th style={th}>비고</th>
                  {/* 이슈1e — ADMIN '삭제됨 포함' 모드의 복구/완전삭제 +
                      round9(요구8) canWriteSales 사용자의 편집 연필을 겸하는 관리 열. */}
                  {showActionsColumn && <th style={th}>관리</th>}
                  {/* PDF §Phase B 페이지 2 §4 — 첫 화면에서 제거된 컬럼:
                      담당자 / 직함 / 연락처 / 가스 타입 / 세팅 메뉴 /
                      불판 / 특수부위 / 프로파일 / 와이어 그릴 / 첨부.
                      모두 고객사명 클릭 → CustomerDetailPage 의 보강된
                      섹션에서 노출. */}
                </tr>
              </thead>
              <tbody>
                {groups
                  ? groups.map((g) => (
                      <GroupBlock
                        key={g.label}
                        label={g.label}
                        meta={g.meta}
                        rows={g.rows}
                        priceVisible={priceVisible}
                        canEditStatus={canEditStatus}
                        onStatusChange={(id, status) => handleStatusChange(id, status)}
                        updatingRowId={updatingRowId}
                        showActions={showActionsColumn}
                        showDeleteActions={showActions}
                        canEditRow={canWriteSales}
                        onEdit={(r) => setEditing(r)}
                        onRestore={(id) => restoreMx.mutate(id)}
                        onPurge={(r) => setPurgeFor(r)}
                        actionBusyId={actionBusyId}
                        salesOwnerFilter={filters.salesOwnerCode}
                        onFilterSalesOwner={(code) =>
                          setFilters((f) => ({
                            ...f,
                            salesOwnerCode: f.salesOwnerCode === code ? undefined : code,
                            page: 1,
                          }))
                        }
                      />
                    ))
                  : sortedItems.map((r) => (
                      <Row
                        key={r.id}
                        row={r}
                        priceVisible={priceVisible}
                        canEditStatus={canEditStatus}
                        onStatusChange={(status) => handleStatusChange(r.id, status)}
                        statusUpdating={updatingRowId === r.id}
                        showActions={showActionsColumn}
                        showDeleteActions={showActions}
                        canEditRow={canWriteSales}
                        onEdit={(r2) => setEditing(r2)}
                        onRestore={(id) => restoreMx.mutate(id)}
                        onPurge={(r2) => setPurgeFor(r2)}
                        actionBusy={actionBusyId === r.id}
                        salesOwnerFilter={filters.salesOwnerCode}
                        onFilterSalesOwner={(code) =>
                          setFilters((f) => ({
                            ...f,
                            salesOwnerCode: f.salesOwnerCode === code ? undefined : code,
                            page: 1,
                          }))
                        }
                      />
                    ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 추가수정 2차(26-07-27) §1 (결정 ①A) — 시트 하단 페이지 버튼. 로봇·계약
          목록과 같은 공용 Pagination 부품을 그대로 붙인다(setFilters 로 page 갱신).
          상단 "총 N곳" 은 유지 — 여기 요약("1–100 / 총 108건")은 지금 보고 있는
          구간을 알려주는 별개 정보다. 마지막 페이지가 비면 부품이 자동 클램프한다.
          마감 차수(26-08-24) — 지역/국가 칩이 켜져 있으면 이 요약도 걸러낸 수를 말한다
          (머리와 **같은 정본 문구**). 종전엔 표에 20행뿐인데 여기가 '1–121 / 총 121건'
          이라고 해서 목록이 잘린 것처럼 읽혔다. */}
      <Pagination
        page={filters.page ?? 1}
        pageSize={filters.pageSize ?? PAGE_SIZE}
        // 머리 정렬 중에는 화면이 페이지를 나눈다 — 받아 온(걸러 낸) 행 수가 기준이다.
        total={colSortedAll ? colSortedAll.length : total}
        onPageChange={(p) => setFilters((f) => ({ ...f, page: p }))}
        onPageSizeChange={(s) => setFilters((f) => ({ ...f, page: 1, pageSize: s }))}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        totalSuffix={chipSuffixForPager ?? undefined}
      />

      {/* docs/plan/customer-master-ux/plan.md §3 — 신규 등록 / 편집 modal
          (같은 컴포넌트, editing prop 으로 모드 결정 — Phase 3 통합).
          편집은 목록 행의 연필 버튼으로만 진입한다. */}
      <CustomerMasterCreateModal
        open={createOpen || editing !== null}
        editing={editing ?? undefined}
        // round11 (요구 11) — 현재 탭이 국가 기본값/검증을 정한다.
        scope={scope ?? 'domestic'}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
        }}
      />

      {/* FB §4b — '회수 예정' 전환 시 회수 사유 필수 입력 모달. */}
      <ReturnReasonModal
        open={returnReasonFor !== null}
        customerName={returnReasonFor?.name ?? ''}
        busy={updateStatusMx.isPending}
        onCancel={() => setReturnReasonFor(null)}
        onConfirm={(reason) => {
          if (!returnReasonFor) return;
          updateStatusMx.mutate(
            { id: returnReasonFor.id, status: 'return_planned', reason },
            { onSuccess: () => setReturnReasonFor(null) },
          );
        }}
      />

      {/* 이슈1e — 완전삭제(퍼지) 확인 모달. 고객사명을 정확히 입력해야 활성화
          (서버도 confirmName 재검증). 연쇄 삭제이므로 되돌릴 수 없음. */}
      <PurgeConfirmModal
        row={purgeFor}
        busy={purgeMx.isPending}
        onCancel={() => setPurgeFor(null)}
        onConfirm={(name) => {
          if (purgeFor) purgeMx.mutate({ id: purgeFor.id, name });
        }}
      />
    </div>
  );
}

// 이슈1e — 완전삭제 확인 모달. 고객사 displayName 을 정확히 입력해야 '완전
// 삭제' 버튼이 활성화된다(서버가 confirmName 으로 한 번 더 검증). 연쇄(cascade)
// 삭제라 되돌릴 수 없음을 경고.
function PurgeConfirmModal({
  row,
  busy,
  onCancel,
  onConfirm,
}: {
  row: CustomerMasterRow | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}) {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    setTyped('');
  }, [row]);
  const target = row?.displayName ?? '';
  const matches = typed.trim() === target && target.length > 0;
  return (
    <Modal
      open={row !== null}
      onClose={onCancel}
      title="고객사 완전 삭제"
      width={480}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            취소
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={() => onConfirm(typed.trim())}
            disabled={busy || !matches}
          >
            {busy ? '삭제 중…' : '완전 삭제'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-danger)' }}>
          이 작업은 되돌릴 수 없습니다. 고객사와 관련된 계약, 발주, 방문, 서명, 리포트, 매장이 함께
          삭제되고, 배정된 로봇은 출고 대기로 돌아갑니다.
        </p>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-muted)' }}>
          확인을 위해 고객사명을 그대로 입력해 주세요:{' '}
          <strong style={{ color: 'var(--fg-default)' }}>{target}</strong>
        </p>
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={target}
          aria-label="삭제 확인용 고객사명"
        />
      </div>
    </Modal>
  );
}

// FB §4b — 회수 예정 사유 입력 모달. 사유가 비어 있으면 확정 버튼 비활성화
// (window.prompt 대신 깔끔한 confirm-with-text). 모달이 열릴 때마다 입력 초기화.
function ReturnReasonModal({
  open,
  customerName,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  customerName: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const trimmed = reason.trim();
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="회수 예정 사유 입력"
      width={460}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            취소
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={() => onConfirm(trimmed)}
            disabled={busy || trimmed.length === 0}
          >
            회수 예정으로 변경
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-muted)' }}>
          {customerName ? `'${customerName}' 고객사를 ` : ''}회수 예정으로 바꿉니다. 회수 사유는 꼭
          입력해 주세요.
        </p>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="예) 계약 만료 / 운영 중단 / 폐업 등"
          aria-label="회수 사유"
        />
      </div>
    </Modal>
  );
}

function GroupBlock({
  label,
  meta,
  rows,
  priceVisible,
  canEditStatus,
  onStatusChange,
  updatingRowId,
  showActions,
  showDeleteActions,
  canEditRow,
  onEdit,
  onRestore,
  onPurge,
  actionBusyId,
  salesOwnerFilter,
  onFilterSalesOwner,
}: {
  label: string;
  meta: string;
  rows: CustomerMasterRow[];
  priceVisible: boolean;
  canEditStatus: boolean;
  onStatusChange: (id: string, status: CustomerMasterStatus | null) => void;
  updatingRowId: string | null;
  // round9 (요구 8) — showActions 는 열 표시 여부(복구/완전삭제 또는 편집 어느
  // 쪽이든 하나라도 필요하면 true). showDeleteActions 는 그중 삭제행 복구/완전
  // 삭제 액션 자체의 게이트(기존 isAdmin && includeDeleted). canEditRow 는
  // 편집 연필의 게이트. 셋을 분리해야 "삭제됨 포함"을 켜기 전에는 (설령 mock/캐시에
  // deletedAt 행이 섞여 있어도) 복구 버튼이 조기 노출되지 않는다.
  showActions: boolean;
  showDeleteActions: boolean;
  canEditRow: boolean;
  onEdit: (row: CustomerMasterRow) => void;
  onRestore: (id: string) => void;
  onPurge: (row: CustomerMasterRow) => void;
  actionBusyId: string | null;
  salesOwnerFilter?: string;
  onFilterSalesOwner?: (code: string) => void;
}) {
  return (
    <>
      <tr>
        {/* PDF §Phase B 페이지 2 §2 — 폴더 픽토그램 제거. 깔끔한 헤더만.
            수정 사항2.pdf p.2 #1 — head 의 담당자/직함/연락처 3개 제거 후
            전체 컬럼 수 = 20 으로 colSpan 동기화.
            운영자 피드백 (round 3) — '방향' 컬럼 제거 → colSpan 20 → 19.
            이슈1e — '삭제됨 포함' 모드 또는 round9(요구8) 편집 연필로 관리 열이
            추가되면 colSpan 19 → 20.
            계약 사실화 1단계 정정6(26-07-29) — '계약 형태' 컬럼 신설 → 20 → 21
            (관리 열 없으면 19 → 20). 헤더/행/이 colSpan 세 곳이 함께 움직여야 그룹
            제목 줄이 표 폭과 어긋나지 않는다.
            개선 9건 차수2(26-08-04) §7 — 지원금·소개비 2칸 신설 → 21 → 23
            (관리 열 없으면 20 → 22).
            계획서 요구 2a §2(26-08-21) — '회수일' 칸 신설 → 23 → 24 (관리 열 없으면
            22 → 23). 헤더/행/이 colSpan 세 곳의 정합은 시험이 지킨다('그룹 머리의
            colSpan 이 실제 칸 수와 같다'). */}
        <td colSpan={showActions ? 24 : 23} style={groupHeaderTd}>
          <strong>{label}</strong>
          <span style={{ color: 'var(--fg-muted)', fontSize: 11, marginLeft: 8 }}>{meta}</span>
        </td>
      </tr>
      {rows.map((r) => (
        <Row
          key={r.id}
          row={r}
          priceVisible={priceVisible}
          canEditStatus={canEditStatus}
          onStatusChange={(status) => onStatusChange(r.id, status)}
          statusUpdating={updatingRowId === r.id}
          showActions={showActions}
          showDeleteActions={showDeleteActions}
          canEditRow={canEditRow}
          onEdit={onEdit}
          onRestore={onRestore}
          onPurge={onPurge}
          actionBusy={actionBusyId === r.id}
          salesOwnerFilter={salesOwnerFilter}
          onFilterSalesOwner={onFilterSalesOwner}
        />
      ))}
    </>
  );
}

function Row({
  row,
  priceVisible,
  canEditStatus,
  onStatusChange,
  statusUpdating,
  showActions,
  showDeleteActions,
  canEditRow,
  onEdit,
  onRestore,
  onPurge,
  actionBusy,
  salesOwnerFilter,
  onFilterSalesOwner,
}: {
  row: CustomerMasterRow;
  priceVisible: boolean;
  canEditStatus: boolean;
  onStatusChange: (status: CustomerMasterStatus | null) => void;
  statusUpdating: boolean;
  showActions: boolean;
  // round9 (요구 8) — 상세 페이지 진입 없이 목록에서 바로 편집 모달을 여는
  // 연필 버튼. 삭제된 행은 showDeleteActions 가 true 일 때만 복구/완전삭제를
  // 보여주고, 그 외(활성 행, 또는 아직 "삭제됨 포함"을 켜지 않은 경우)엔
  // canEditRow 게이트로 편집 연필 또는 대시.
  showDeleteActions: boolean;
  canEditRow: boolean;
  onEdit: (row: CustomerMasterRow) => void;
  onRestore: (id: string) => void;
  onPurge: (row: CustomerMasterRow) => void;
  actionBusy: boolean;
  // round8 (요구 4) — 영업 담당 셀 클릭→필터. 현재 필터값 + 토글 콜백.
  salesOwnerFilter?: string;
  onFilterSalesOwner?: (code: string) => void;
}) {
  const statusColor = CUSTOMER_MASTER_STATUS_COLOR[row.status];
  // 다계약 표시 차수3(26-08-05) — 계약기간·도입일·반납일·D-Day 네 칸이 보는 계약.
  // 종전엔 반납일·D-Day 만 임박 계약을 보고 계약기간·도입일은 최신 계약을 봐서, 한 행의
  // 네 칸이 두 계약을 말했다. 이제 오너 사양대로 **먼저 끝나는 계약**(판매 전용은 후보에서
  // 빠진다) 한 건을 따른다 — 판정은 서버 한 벌(resolveTermBasis 머리 주석에 폴백 규칙과 근거).
  // ⚠ '판매 전용이 아닌'을 '구독'이라 줄여 부르지 않는다 — 줄 0개(형태 미상) 계약도 후보다.
  const termBasis = resolveTermBasis(row);
  const termFacts = termBasis.facts;
  const allContracts = row.contracts ?? [];
  // 개선 9건 차수2f(26-08-04) — 금액 5칸·계약 대수는 이 자리 하나만 본다.
  const totals = rowTotals(row);
  // 다계약 표시 차수3 정정(26-08-06) — 두 합집합 칸의 값과 **그 값의 출처**를 한 번만 판정하고
  // 칸과 배지 안내가 그 결과를 함께 읽는다(판정이 두 벌이면 칸은 종전 값을 보여 주면서 안내는
  // '전 계약을 합쳤다'고 말한다 — 그것이 이번에 닫는 결함이다).
  const robotType = robotTypeCell(row);
  const contractType = contractTypeCell(row);
  // 무엇을 보고 있는지 밝혀야 하는 행: 계약이 여럿이거나(한 칸이 한 계약만 말한다),
  // 진행 중 계약이 없어 최신 계약 하나로 폴백했거나(합계의 뜻이 다르다),
  // 합계를 못 받아 칸이 비었거나(왜 비었는지 말해야 한다),
  // 판매뿐이라 네 칸이 비었거나(빈 칸의 이유를 말하지 않으면 계약이 누락된 줄 안다).
  // 정정3(26-08-06) — 마지막 갈림길은 **판정 정본**(termBasisNoReturnReason)이 한다. 값이
  // 하나 늘 때(live-sale-only) 이 자리에 리터럴 비교가 남아 있으면 배지가 조용히 사라진다.
  const needsBasisNotice =
    allContracts.length > 1 ||
    totals?.basis === 'latest-fallback' ||
    totalsMissing(row) ||
    termBasisNoReturnReason(termBasis.reason) !== null;
  // 표기는 formatDdayOrNull 정본 한 벌 — 다계약 안내가 같은 글자를 쓴다.
  const ddayText = formatDdayOrNull(termFacts?.daysRemaining) ?? '–';
  const ddayColor =
    termFacts?.daysRemaining !== null && termFacts?.daysRemaining !== undefined
      ? termFacts.daysRemaining < 0
        ? 'var(--fg-danger)'
        : termFacts.daysRemaining <= 30
          ? 'var(--dday-warning)'
          : undefined
      : undefined;

  return (
    <tr style={tr}>
      {/* 1. No. — 편집 아이콘 제거 (관리 열로 이동) */}
      <td style={tdMono}>{row.no}</td>
      {/* 2. 상호 — 클릭 시 고객사 상세 페이지 (편집 + 삭제 가능). */}
      <td style={tdEmphasis}>
        <Link to={`/customers/${row.id}`} style={linkStyle}>
          {row.displayName || '(미입력)'}
        </Link>
        {/* 이슈1e — 삭제된 고객사 배지. */}
        {row.deletedAt && <span style={deletedBadge}>삭제됨</span>}
      </td>
      {/* 3. 위치 — S1 Phase 9 (R2 #7): 지도 URL N/G 아이콘. */}
      <td style={td}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>{row.address || row.region || '–'}</span>
          <MapUrlIcon url={row.addressMapUrl} />
        </div>
      </td>
      {/* PDF §Phase B 페이지 2 §4 — 담당자 / 직함 / 연락처 컬럼 제거.
          CustomerDetailPage 의 "담당자" 섹션에서 전체 노출. */}
      {/* 4. 매장 수 — round3 #3: 사용자 입력값(expectedStoreCount) 우선 표시,
          없으면 실제 등록 매장 수(storeCount)로 폴백. (신규 고객사는 Store 가
          자동 생성되지 않아 storeCount=0 이라 입력값이 0 으로 보이던 버그.) */}
      <td style={tdMono}>{row.expectedStoreCount ?? row.storeCount}</td>
      {/* 8. 영업 담당 — round8 (요구 4) 클릭→salesOwnerCode 필터 토글. */}
      <td style={tdMono}>
        {row.salesOwnerCode && onFilterSalesOwner ? (
          <button
            type="button"
            title={`영업 담당 '${row.salesOwnerCode}' 로 필터`}
            aria-pressed={salesOwnerFilter === row.salesOwnerCode}
            onClick={() => onFilterSalesOwner(row.salesOwnerCode as string)}
            style={filterCellStyle(salesOwnerFilter === row.salesOwnerCode)}
          >
            {row.salesOwnerCode}
          </button>
        ) : (
          (row.salesOwnerCode ?? '–')
        )}
      </td>
      {/* 9. 렌탈사 — 운영자 피드백 (round 3) 으로 'AJ Boolean' 에서 자유
          입력 string 으로 확장. row.rentalCompany 가 있으면 그 값, 없으면
          기존 hasAJ Boolean 에서 fallback (true→O, false→X). */}
      <td style={td}>
        {row.rentalCompany ?? (row.hasAJ === true ? 'O' : row.hasAJ === false ? 'X' : '–')}
      </td>
      {/* 10. 보증 보험 */}
      <td style={tdCenter}>{formatBool(row.hasInsurance)}</td>
      {/* round5 #22 — 11. 희망 도입일 cell 제거 (헤더 주석 참조). */}
      {/* 12. 모델 — 다계약 표시 차수3(26-08-05): 전 계약 합집합(서버가 합친 값을 그대로).
          종전엔 최신 계약 1건의 줄만 말해서 슭돈 이대역본점이 'ZEPHYR × 1' 로만 보였다. */}
      <td style={td} data-testid="cm-robot-type">
        {robotType.text}
      </td>
      {/* 계약 사실화 1단계 정정6(26-07-29) — 12.5 계약 형태(줄들의 요약). 단일이면 한
          단어('구독'), 혼합이면 대수까지('판매 1 · 구독 2'). 문구는 표기 정본이 정한다
          (formatContractTypeCell 주석 = 이 칸이 왜 스윕 동선인지).
          정정9(26-07-29) — 오너 지시로 판매/구독 두 단어만 보인다(파일럿·시연·행사·
          사내·금융리스는 '구독'). 저장·감사 로그는 7값 원문 그대로. */}
      <td style={td} data-testid="cm-contract-type">
        {contractType.text}
      </td>
      {/* 13. 계약기간 — 다계약 표시 차수3(26-08-05): 네 칸 중 하나라 **기준 계약**(먼저 끝나는
          계약 · 판매 전용 제외)을 본다. 종전엔 최신 계약이라 판매 계약을 새로 등록하면 이 칸이
          그 판매 계약의 기간으로 바뀌었다. */}
      <td style={tdMono}>
        <TermBasisTip rowId={row.id} column="rentalTerm" basis={termBasis} contracts={allContracts}>
          {formatRentalTerm(termFacts?.rentalTermMonths)}
        </TermBasisTip>
      </td>
      {/* 14. 계약 대수 — 개선 9건 차수2e(26-08-04) ⑤: **전 계약 합**
          (contractedRobotCount 정본. 종전엔 최신 계약 1건이라 구독 1대 + 추가 판매 1대인
          고객사가 '1대'로 보였다). 계약이 여럿이면 반납일 칸의 '계약 N건' 배지가 이 칸이
          합계라는 것을 말한다. */}
      <td style={tdMono} data-testid="cm-contract-qty">
        {contractedRobotCount(row) ?? '–'}
      </td>
      {/* 15. 계약일 — 최신 계약(편집·저장이 닿는 계약) 기준. 네 칸과 **일부러 다르다**:
          이 칸은 "이 고객사와 마지막으로 맺은 계약이 언제인가"를 말한다. 기본 정렬이 쓰는
          '처음 계약일'과도 다른 날일 수 있어, 계약이 여럿이면 배지 안내가 그 값을 적는다. */}
      <td style={tdMono}>{formatDate(row.contract?.contractedAt)}</td>
      {/* round5 #22 — 15.5 도입일(설치 완료): InfraOrder.installCompletedAt 우선, 없으면
          그 계약에 예약된 로봇의 배치일(서버 파생).
          다계약 표시 차수3(26-08-05) — 네 칸 중 하나라 **기준 계약**을 본다. */}
      <td style={tdMono}>
        <TermBasisTip
          rowId={row.id}
          column="installCompletedAt"
          basis={termBasis}
          contracts={allContracts}
        >
          {formatDate(termFacts?.installCompletedAt)}
        </TermBasisTip>
      </td>
      {/* 16. 반납일 — 다계약 표시 차수3(26-08-05): 먼저 끝나는 **구독** 계약 기준(판매는
          소유가 넘어가 반납이 없다). 계약이 여럿이면 이 칸에 붙는 '계약 N건' 배지가 칸별
          기준을 전부 밝히고, 네 칸의 툴팁이 다른 계약의 같은 항목을 보여 준다. */}
      <td style={tdMono}>
        <TermBasisTip rowId={row.id} column="endDate" basis={termBasis} contracts={allContracts}>
          {formatDate(termFacts?.endDate)}
        </TermBasisTip>
        {/* 계획서 요구 2a §1(26-08-21) — 만료 배지. 판정은 정본 한 벌(isEndDateExpired —
            기준 계약의 EXPIRED 정확 일치, TERMINATED 는 배지 없음). 색 + '만료' 글자 —
            색만으로 뜻을 전하지 않는다(화면 규칙). D-Day 의 D+N 표기는 그대로다: 두 신호가
            같은 기준 계약에서 나오므로 모순될 수 없다. */}
        {isEndDateExpired(row) && (
          <span style={expiredBadge} data-testid="cm-expired-badge">
            만료
          </span>
        )}
        {needsBasisNotice && (
          <MultiContractBadge
            rowId={row.id}
            contracts={allContracts}
            totals={totals}
            termBasis={termBasis}
            firstContractedAt={row.firstContractedAt}
            unionCells={{ robotType: robotType.union, contractType: contractType.union }}
          />
        )}
      </td>
      {/* 17. D-Day */}
      <td style={{ ...tdMono, color: ddayColor, fontWeight: ddayColor ? 600 : undefined }}>
        <TermBasisTip rowId={row.id} column="dday" basis={termBasis} contracts={allContracts}>
          {ddayText}
        </TermBasisTip>
      </td>
      {/* 17.5 회수일 — 계획서 요구 2a §2(26-08-21): 반납일·D-Day 바로 옆(수명주기 묶음).
          확정/예정(흐린 톤)/'–' 3형은 표기 정본 한 벌(returnDateCellValue — CSV 와 같은
          글자). 로봇별 회수 기록은 hover 툴팁이 아니라 기존 '누르는 안내' 관례
          (DisclosureTip — 키보드로도 열린다, 화면 규칙 절). 입력은 지금 자리 그대로
          (예정일은 필드팀 탭, 실제 회수는 회수 처리 때 자동 기록) — 이 칸은 보기만 한다. */}
      <td style={tdMono} data-testid="cm-return-date">
        <ReturnDateCell rowId={row.id} info={row.returnInfo} />
      </td>
      {/* 18~22. 금액 5칸 — 개선 9건 차수2f(26-08-04): 전부 **합계 자리**(row.totals)에서
          온다. 종전엔 row.contract 의 같은 이름 칸을 읽었는데, 그 자리는 편집 창이
          프리필하고 저장이 닿는 '그 계약'이라 합계를 담아 둘 곳이 아니었다.
          모양은 종전과 같다: 가격 권한이 없으면 '—'(마스킹이 앞), 있으면 표기 정본이
          0·빈 값을 '–' 로 접는다(formatKRW → lib/amount-or-none).
          합계를 못 받으면 다섯 칸이 모두 '–' 이고, 왜 비었는지는 표 위 안내와 이 행의
          배지가 말한다 — 옛 계산으로 되돌리지 않는다. */}
      <td style={tdMono}>{priceVisible ? formatKRW(totals?.subscriptionFeeKrw) : '—'}</td>
      <td style={tdMono}>{priceVisible ? formatKRW(totals?.salesPriceKrw) : '—'}</td>
      <td style={tdMono}>{priceVisible ? formatKRW(totals?.depositKrw) : '—'}</td>
      <td style={tdMono}>{priceVisible ? formatKRW(totals?.subsidyKrw) : '—'}</td>
      <td style={tdMono}>{priceVisible ? formatKRW(totals?.referralFeeKrw) : '—'}</td>
      {/* 21. 진행 상태 — PDF §Phase B §3: 인라인 편집 dropdown.
          canEditStatus 이면 select, 아니면 read-only pill. select 의 value 가
          row.status (synthesizeStatus 또는 manualStatusOverride 결과).
          변경 시 onStatusChange(newStatus) — 같은 값을 다시 선택해도 mutation
          은 firing (서버에서 manualStatusOverride 가 비어있어 자동 계산되던
          경우 명시적으로 manual override 로 전환 가능). */}
      <td style={td}>
        <StatusEditCell
          status={row.status}
          pinned={row.statusPinned}
          color={statusColor}
          canEdit={canEditStatus}
          busy={statusUpdating}
          onChange={onStatusChange}
        />
      </td>
      {/* PDF §Phase B 페이지 2 §4 — 다음 컬럼 첫 화면에서 제거, detail
          로 이관: 가스 타입 / 세팅 메뉴 / 불판 / 특수부위 / 프로파일 /
          와이어 그릴 / 첨부. */}
      {/* 운영자 피드백 (round 3) — '방향' td 도 제거 (head 와 동기화).
          direction 은 CustomerDetailPage 의 운영 사양 섹션에서 확인. */}
      {/* 22. 비고 */}
      <td style={td} title={row.notes ?? ''}>
        {row.notes ? (row.notes.length > 30 ? row.notes.slice(0, 30) + '…' : row.notes) : '–'}
      </td>
      {/* 이슈1e — 관리: 삭제된 행은 복구/완전삭제(ADMIN). round9(요구8) —
          활성 행은 canEditRow 사용자에게 편집 연필(상세 페이지 진입 없이
          CustomerMasterCreateModal 을 바로 연다, RobotsListPage 와 동형). */}
      {showActions && (
        <td style={td}>
          {row.deletedAt && showDeleteActions ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => onRestore(row.id)}
                disabled={actionBusy}
                style={restoreActionBtn}
              >
                {actionBusy ? '처리 중…' : '복구'}
              </button>
              <button
                type="button"
                onClick={() => onPurge(row)}
                disabled={actionBusy}
                style={purgeActionBtn}
              >
                완전삭제
              </button>
            </div>
          ) : canEditRow && !row.deletedAt ? (
            <button
              type="button"
              aria-label="편집"
              title="편집"
              onClick={() => onEdit(row)}
              style={editActionBtn}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </button>
          ) : (
            <span style={{ color: 'var(--fg-disabled)' }}>–</span>
          )}
        </td>
      )}
    </tr>
  );
}

// ============================================================
// 개선 9건 차수2e(26-08-04) ⑤ — 열리는 안내 한 벌(배지 · 네 칸 툴팁이 공유)
// ============================================================
//
// **접근성** — 종전엔 native `title` 툴팁이었다. 마우스 hover 로만 열려서 키보드·터치
// 사용자에게는 "계약이 2건이다"라는 사실만 보이고 **그 내용을 읽을 방법이 없었다**.
// 이 파일이 이미 쓰는 패턴을 그대로 쓴다(StatusDropdown: 버튼 + aria-expanded +
// 바깥 mousedown 닫기 + Esc 닫기 — EntityPicker 에서 온 패턴). 새 부품을 만들지 않는다.
// hover 로 열리는 종전 감각도 남긴다(마우스로 지나가면 열리고 벗어나면 닫힌다. 클릭·
// 키보드로 연 것은 마우스가 벗어나도 닫지 않는다 — 읽는 중에 사라지면 안 된다).
//
// 다계약 표시 차수3(26-08-05) — 여는 자리가 둘이 됐다(배지 + 네 칸 툴팁). 여닫는 규칙을
// 두 벌로 적으면 한쪽만 키보드로 열리거나 한쪽만 Esc 로 닫히는 어긋남이 생기므로 기계는
// 여기 한 벌(DisclosureTip)이고, 두 자리는 **무엇을 적는지**만 다르다.

function DisclosureTip({
  panelId,
  panelLabel,
  trigger,
  triggerTestId,
  triggerStyle,
  panelStyle,
  children,
}: {
  panelId: string;
  panelLabel: string;
  /** 버튼에 보이는 것 — 배지는 글자, 네 칸 툴팁은 그 칸의 값 그대로. */
  trigger: ReactNode;
  triggerTestId?: string;
  triggerStyle: CSSProperties;
  panelStyle: CSSProperties;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // 클릭·키보드로 연 것인가(그렇다면 마우스가 벗어나도 유지).
  const stickyRef = useRef(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        stickyRef.current = false;
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onDocMouseDown);
    return () => window.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  function close(): void {
    stickyRef.current = false;
    setOpen(false);
  }

  return (
    <span ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        data-testid={triggerTestId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          if (open && stickyRef.current) {
            close();
            return;
          }
          stickyRef.current = true;
          setOpen(true);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => {
          if (!stickyRef.current) setOpen(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) {
            // 시트 위 다른 Esc 처리(모달 등)로 새어 나가지 않게 여기서 멈춘다.
            e.stopPropagation();
            close();
          }
        }}
        style={triggerStyle}
      >
        {trigger}
      </button>
      {open && (
        <div id={panelId} role="group" aria-label={panelLabel} style={panelStyle}>
          {children}
        </div>
      )}
    </span>
  );
}

/**
 * 네 칸의 값 한 줄 — 계약기간 · 도입일 · 반납일(D-Day). 시트 칸과 같은 표기 정본을 쓴다.
 *
 * 다계약 표시 차수3 정정(26-08-06) — `saleOnly` 인 계약에는 **반납일·D-Day 를 적지 않는다.**
 * 판매 전용 계약도 DB 에 endDate 를 갖고 있어서 종전엔 '반납일 2026.09.30 (D-55)' 가 그대로
 * 찍혔고, 같은 패널 꼬리말은 "판매는 소유가 고객사로 넘어가 반납일이 없습니다"라고 단정했다 —
 * 한 패널이 동시에 참일 수 없는 두 문장을 말했다. 운영 위험이 구체적이다: 운영자가 그 D-55 를
 * 회수 임박으로 읽고 회수 일정을 잡는데, 그 로봇은 이미 고객사 소유라 회수할 것이 없다.
 *
 * 정정2(26-08-06) — 그 반납일 조각(날짜 대신 없는 이유 / 날짜 + D-Day)은 이제 **lib 정본
 * 한 벌**이다(endDateFieldText). 고객사 상세의 계약 카드가 같은 규칙을 읽는다 — 종전엔 이
 * 규칙이 이 파일 안에만 있어서 상세 카드가 판매 계약의 'D-1094' 를 그대로 찍었다.
 *
 * 계약기간·도입일은 그대로 적는다 — 그 두 값은 그 계약에 **실제로 일어난 사실**이고(판매도
 * 설치는 한다), 없는 사건을 말하는 것은 반납일·D-Day 쪽뿐이다.
 */
function termFactsText(
  f: {
    rentalTermMonths: number | null;
    installCompletedAt: string | null;
    endDate: string | null;
    daysRemaining: number | null;
  },
  saleOnly: boolean,
): string {
  return [
    `${COLUMN_LABEL.rentalTerm} ${formatRentalTerm(f.rentalTermMonths)}`,
    `${COLUMN_LABEL.installCompletedAt} ${formatDate(f.installCompletedAt)}`,
    // 칸 이름은 이 시트의 열 이름 정본에서 넘긴다(정본에 리터럴로 박으면 열 이름을 고친 날
    // 안내문만 옛 이름을 말한다 — COLUMN_LABEL 주석의 그 드리프트).
    endDateFieldText(COLUMN_LABEL.endDate, f, saleOnly),
  ].join(' · ');
}

/**
 * 이 계약이 **판매 전용**인가 — 판정 정본(lib/term-basis → shared isSaleOnlyContract)에
 * 서버가 내린 줄 원문(contracts[].robots[].reason)을 넘겨 얻는다. 화면이 새로 판정하지 않는다.
 * 줄이 0개거나 줄을 내리지 않는 응답에서는 판매 전용이 아니다(정본 주석의 근거).
 */
function isSaleOnlySummary(c: CustomerMasterContractSummary): boolean {
  return isSaleOnlyByLines(c.robots);
}

/**
 * 다계약 표시 차수3(26-08-05) — 네 칸(계약기간·도입일·반납일·D-Day)의 **타 계약 안내**.
 *
 * 오너 요청(26-08-05): "그 4칸에 마우스를 올리면 타 계약의 정보도 나온다."
 *
 * 무엇을 담고 무엇을 담지 않는가 — 같은 행의 '계약 N건' 배지가 이미 계약별 형태·대수·배정
 * 단계·계약일과 합계 셈법을 적는다. 그것을 여기 또 적으면 두 안내가 같은 말을 두 번 한다.
 * 그래서 이 툴팁은 그 네 칸에 필요한 것만 담는다:
 *   ① **지금 어느 계약을 보고 있는가** — 먼저 말한다(그 다음에야 다른 계약이 뜻을 갖는다).
 *   ② 다른 계약들의 **같은 네 항목**. 그중 계약기간은 배지 안내에 없는 값이다.
 *   ③ 네 칸이 판매 전용 계약을 후보에서 빼는 **이유** — 판매는 소유가 넘어가 반납이 없다.
 *      이 문장이 없으면 판매 계약 옆의 빈 칸이 '누락'으로 읽힌다.
 *
 * 계약이 1건이면 붙지 않는다(오너: 소음). 그 경우 이 부품은 값을 그대로 돌려주므로 시트의
 * DOM 이 종전과 같다 — 대다수 행이 그렇다.
 */
function TermBasisTip({
  rowId,
  column,
  basis,
  contracts,
  children,
}: {
  rowId: string;
  /** 어느 칸에서 열렸는가 — 패널 id 를 행·칸으로 만들고 안내 제목에 그 칸 이름을 쓴다. */
  column: 'rentalTerm' | 'installCompletedAt' | 'endDate' | 'dday';
  basis: TermBasis;
  contracts: CustomerMasterContractSummary[];
  children: ReactNode;
}) {
  if (contracts.length <= 1) return <>{children}</>;
  const basisId = basis.facts?.id ?? null;
  const others = contracts.filter((c) => c.id !== basisId);
  const label = COLUMN_LABEL[column];
  return (
    <DisclosureTip
      panelId={`cm-term-basis-${column}-${rowId}`}
      // 기준 계약이 없는 고객사(전부 판매)의 패널 이름이 없는 계약을 부르지 않게 갈린다 —
      // 화면 낭독으로만 이 안내를 듣는 사람에게도 같은 사실이 가야 한다.
      panelLabel={
        basis.facts
          ? `${label}: 기준 계약과 다른 계약의 같은 항목`
          : `${label}: 이 칸이 빈 이유와 이 고객사의 계약`
      }
      triggerTestId={`cm-term-basis-tip-${column}`}
      triggerStyle={termBasisTipTrigger}
      panelStyle={termBasisTipPanel}
      trigger={children}
    >
      {basis.facts ? (
        <>
          {/* 🔴 정정5(26-08-06) — 종전 이 자리는 기준 계약을 '(먼저 끝나는 **구독** 계약)'이라
              불렀다. 서버의 후보 규칙은 '구독'이 아니라 '판매 전용이 아닌 것'이라, **줄이 0개인
              계약**(계약 조건만 먼저 입력한 계약 — 형태 미상)이 기준으로 뽑힌 행에서 이 머리말이
              '구독 계약'이라 단정하는 동안 같은 행의 '계약 N건' 배지는 그 계약을 '–'(미상)로
              적고 있었다. 계약이 전부 해지된 고객사(서버 폴백)에서도 거짓이었다. 이제 후보
              **규칙**을 적는다 — 정본 한 벌(lib/term-basis)이고 두 경우 모두 참이다. */}
          <p style={{ margin: '0 0 4px' }}>
            이 네 칸이 보는 계약은 <strong>{basis.facts.humanId}</strong>입니다.{' '}
            {basis.reason === 'legacy-latest'
              ? '서버가 기준 계약을 따로 보내 주지 않아 최신 계약을 보여 줍니다.'
              : `(${TERM_BASIS_PICK_RULE_TEXT})`}
          </p>
          {/* 기준 계약의 줄은 **시트 칸과 같은 값**을 적는다(반납일을 접지 않는다). 새 서버가
              고른 기준 계약은 판매 전용이 아니고(서버가 규칙으로 뺀다), 구버전 서버에서 최신
              계약이 판매여도 그 날짜는 옆 칸이 지금 보여 주고 있는 값이라 여기서 감추면 툴팁이
              칸과 다른 말을 한다 — 그 경우의 사정은 위 괄호와 아래 꼬리말이 말한다. */}
          <div style={{ color: 'var(--fg-muted)' }}>{termFactsText(basis.facts, false)}</div>
        </>
      ) : (
        // 정정3(26-08-06) — 이 머리말은 **lib 정본 한 벌**이다. 종전엔 여기 리터럴 두 갈래가
        // 있었고, 'sale-only' 가 아닌 값은 전부 '기준 계약을 받지 못해…' 로 접혀 서버가
        // 정확히 판정한 고객사에까지 "응답이 부실하다"는 뜻의 안내가 나갔다.
        <p style={{ margin: 0 }}>{termBasisBlankCellsTip(basis.reason, '이 네 칸')}</p>
      )}
      {others.length > 0 && (
        <>
          {/* 기준 계약이 없으면(전부 판매 / 계약 없음) 이 목록은 '다른' 계약이 아니라 **이
              고객사의 계약 전부**다 — 비교 대상이 없는데 '다른 계약'이라고 적으면 어딘가에
              기준 계약이 있다는 뜻으로 읽힌다. */}
          <p style={{ margin: '8px 0 0', color: 'var(--fg-muted)' }}>
            {basis.facts ? '다른 계약' : '이 고객사의 계약'}
          </p>
          {others.map((c) => (
            <div key={c.id} style={multiContractPanelRow}>
              <div style={{ fontWeight: 600 }}>
                {c.humanId} · {foldContractTypeForDisplay(c.contractType, c.robots ?? [])}
              </div>
              {/* 판매 전용 계약이면 반납일·D-Day 자리에 **없는 이유**가 온다(termFactsText 의
                  주석에 그 결함과 운영 위험). 판정은 서버가 내린 줄 원문 + shared 정본이다. */}
              <div style={{ color: 'var(--fg-muted)' }}>
                {termFactsText(c, isSaleOnlySummary(c))}
              </div>
            </div>
          ))}
        </>
      )}
      {/* 꼬리말이 이 패널의 다른 줄과 모순되지 않게 갈린다 — 구버전 서버에서는 네 칸이 실제로
          최신 계약을 보고 있으므로 후보 규칙 문장이 그 응답에서는 참이 아니다.
          정정5(26-08-06) — 규칙 문장도 '구독 계약만 따릅니다'에서 정본 한 벌
          (TERM_BASIS_CANDIDATE_RULE_TEXT)로 바꿨다. 혼합 계약·줄 0개 계약도 후보라 '구독만'은
          거짓이었고, 그 거짓이 위 머리말과 같은 뿌리다. */}
      <p style={{ margin: '6px 0 0', color: 'var(--fg-muted)' }}>
        {basis.reason === 'legacy-latest' ? (
          <>
            이 네 칸({joinColumnNames(TERM_BASIS_COLUMNS)})은 {TERM_BASIS_CANDIDATE_RULE_TEXT}. 이
            서버는 그 판정을 내리지 않아 지금은 최신 계약을 보고 있습니다.
          </>
        ) : (
          <>
            이 네 칸({joinColumnNames(TERM_BASIS_COLUMNS)})은 {TERM_BASIS_CANDIDATE_RULE_TEXT}.
          </>
        )}
      </p>
    </DisclosureTip>
  );
}

// ============================================================
// 계획서 요구 2a §2(26-08-21) — '회수일' 칸 + 로봇별 회수 기록의 누르는 안내
// ============================================================
//
// 표기 3형(확정/예정/'–')은 returnDateCellValue 정본이 정하고, 이 부품은 그 글자를 그리고
// **기록이 있을 때만** 누르는 안내(DisclosureTip — 배지·네 칸 툴팁과 같은 여닫는 기계 한 벌,
// 키보드로도 열린다)로 감싼다. hover 툴팁을 쓰지 않는 이유는 그 두 자리와 같다(키보드·터치
// 사용자가 내용을 읽을 수 없다 — 화면 규칙 절이 '누르는 안내 버튼' 관례를 지정했다).
function ReturnDateCell({ rowId, info }: { rowId: string; info: CustomerMasterRow['returnInfo'] }) {
  const cell = returnDateCellValue(info);
  // 예정은 확정과 다른 흐린 톤(화면 규칙) — 색과 함께 '예정' 글자가 뜻을 말하므로
  // 색만으로 구분하지 않는다.
  const value = cell.scheduled ? (
    <span data-testid="cm-return-date-scheduled" style={{ color: 'var(--fg-muted)' }}>
      {cell.text}
    </span>
  ) : (
    <>{cell.text}</>
  );
  const records = info?.records ?? null;
  // 기록이 없으면([]) 열 것도 없다 — 버튼을 만들지 않는다(빈 패널은 소음이다).
  // records: null(응답이 안 실음 — 콘솔 경로에선 안 온다)도 같은 취급: 없는 기록을
  // 지어내지 않고, 값 글자는 그대로 보인다.
  if (!records || records.length === 0) return value;
  return (
    <DisclosureTip
      panelId={`cm-return-records-${rowId}`}
      panelLabel="로봇별 회수 기록"
      triggerTestId="cm-return-records-tip"
      triggerStyle={termBasisTipTrigger}
      panelStyle={termBasisTipPanel}
      trigger={value}
    >
      <p style={{ margin: '0 0 4px', fontWeight: 700 }}>로봇별 회수 기록</p>
      {records.map((r) => (
        // 같은 로봇이 재배치 후 다시 회수될 수 있어 robotId 만으로는 키가 겹친다.
        <div key={`${r.robotId}-${r.returnedAt}`} style={multiContractPanelRow}>
          {/* 짧은 SN 이 없으면 robotId 그대로 — 식별자를 지어내지 않는다. */}
          <span style={{ fontWeight: 600 }}>{r.serialShort ?? r.robotId}</span>
          <span style={{ color: 'var(--fg-muted)' }}> · {formatDate(r.returnedAt)}</span>
        </div>
      ))}
      {/* 일부만 회수된 고객사 — 기록이 있는데 확정 회수일이 비는 이유를 그 자리에서 말한다. */}
      {(info?.outstandingRobotCount ?? 0) > 0 && (
        <p style={{ margin: '6px 0 0', color: 'var(--fg-muted)' }}>
          아직 나가 있는 로봇이 {info!.outstandingRobotCount}대 있습니다. 회수일은 모두 회수된 뒤에
          정해집니다.
        </p>
      )}
    </DisclosureTip>
  );
}

// ============================================================
// 개선 9건 차수2e(26-08-04) ⑤ — '계약 N건' 배지 + 칸별 기준 안내
// ============================================================
//
// **왜 배지 하나가 전부 말하는가** — 이 시트는 23열이다. '한 계약만 말하는 칸'마다 배지를
// 붙이면 계약 2건인 행에서 배지가 7개가 되어 시트가 시끄러워지고, 정작 무엇이 다른지는
// 더 알기 어려워진다. 그래서 기준이 갈리는 사실을 **한 자리에서 전부** 적는다:
//   ① 계약별 사실 — 계약번호 · 형태 · 대수 · 배정 단계 · 계약일 · 도입일 · 반납일 · D-Day
//   ② 어느 계약이 어느 칸의 기준인지 (TERM/STATUS/PRIMARY_BASIS_COLUMNS)
//   ③ 합쳐 말하는 칸이 **무엇을 합한 값인지** (UNION_COLUMNS + basis)
// 다계약 표시 차수3(26-08-05) — 네 칸(계약기간·도입일·반납일·D-Day)에는 오너 요청으로 툴팁이
// 따로 붙었다. 그 툴팁은 **그 네 칸의 계약별 값**만 담고(계약기간은 여기 없는 값이다), 형태·
// 대수·단계·합계 셈법은 계속 이 배지 한 자리에서만 말한다 — 두 안내가 같은 말을 하지 않게.
// ③ 을 열 머리 각주로 달지 않은 근거: 합계가 여러 계약을 더한 값인 것은 계약이 2건 이상인
// 행뿐이고 그런 고객사는 소수다. 열 머리에 달면 계약 1건인 대다수 행에도 영구히 붙어
// (5열 × 전 행) "이 값은 합계"라는 사실 아닌 인상을 남긴다. 배지는 정확히 밝힐 것이 있는
// 행에만 있다.
//
// 개선 9건 차수2f(26-08-04) — 밝힐 것이 셋으로 늘었다(호출부 needsBasisNotice):
//   • 계약이 2건 이상 — 한 칸이 한 계약만 말한다.
//   • 진행 중 계약이 없어 최신 계약 하나로 폴백 — 합계의 뜻이 다르다. 계약이 1건이어도
//     밝힌다(해지·만료된 계약 한 건의 금액을 '현재 값'처럼 읽으면 안 된다).
//   • 합계를 못 받음 — 칸이 왜 비었는지 말한다(옛 계산으로 되돌리지 않는다).
// 어느 계약을 실제로 셌는지는 countedContractIds 가 말한다 — 계약별 줄에 그대로 적는다.
//
// 다계약 표시 차수3(26-08-05) — 넷째가 늘었다: **판매뿐이라 네 칸이 비었다.** 계약이 1건이어도
// 밝힌다 — 그 행의 계약기간·도입일·반납일·D-Day 가 통째로 '–' 인데 이유를 말하지 않으면
// 운영자는 계약이 누락됐다고 읽는다(계약은 살아 있고 반납이라는 사건만 없다).
function MultiContractBadge({
  rowId,
  contracts,
  totals,
  termBasis,
  firstContractedAt,
  unionCells,
}: {
  // 패널의 id 는 **행**의 신원으로 만든다. 종전엔 첫 계약 id 였는데, 계약 요약이 없는
  // 응답(합계만 빠진 서버·구버전 서버)에서는 여러 행이 같은 id 를 쓰게 된다.
  rowId: string;
  contracts: CustomerMasterContractSummary[];
  totals: CustomerMasterRowTotals | null;
  /** 네 칸이 보는 계약 — 시트 칸과 **같은 판정 결과**를 받는다(여기서 다시 고르지 않는다). */
  termBasis: TermBasis;
  /** 이 고객사의 처음 계약일 — 기본 정렬의 키. 시트 '계약일' 칸(최신 계약)과 다를 수 있다. */
  firstContractedAt?: string | null;
  /**
   * 다계약 표시 차수3 정정(26-08-06) — 두 합집합 칸이 **이 응답에서** 실제로 합집합 값을
   * 받았는가(robotTypeCell · contractTypeCell 의 판정 결과 그대로). 여기서 다시 판정하지
   * 않는다 — 그러면 칸이 보는 값과 안내가 말하는 값이 갈릴 수 있다.
   */
  unionCells: { robotType: boolean; contractType: boolean };
}) {
  const countedCount = totals?.countedContractIds.length ?? 0;
  const isFallback = totals?.basis === 'latest-fallback';
  // 정정3(26-08-06) — '지켜볼 반납일이 없다'는 답의 **이유**(판매뿐 / 살아 있는 것만 판매)를
  // 그대로 들고 다닌다. 아래 배지 글자와 안내 문장이 이유별로 갈리기 때문이다 — 참/거짓
  // 하나로 접으면 어느 문장을 쓸지 다시 판정해야 하고, 그 판정이 두 벌이 된다.
  const noReturnReason = termBasisNoReturnReason(termBasis.reason);
  // 합집합을 못 받아 **종전 값(최신 계약 1건)** 을 보여 주고 있는 칸. 이 목록에 든 칸은
  // '전 계약을 합쳐 말하는 칸'에서 빠진다 — 조용히 옛 값을 보여 주면서 합쳤다고 말하지 않는다.
  const fallbackColumns: string[] = [
    ...(unionCells.robotType ? [] : [COLUMN_LABEL.robotType]),
    ...(unionCells.contractType ? [] : [COLUMN_LABEL.contractType]),
  ];
  // 설계상 합쳐 말하는 칸(UNION_COLUMNS)에서 이번 응답이 못 채운 칸만 뺀다. 금액 5칸·계약
  // 대수는 합계 자리 하나에서 오므로 목록에 남고, 그 자리가 없을 때는 아래 unionBasisText 가
  // '비어 있다'고 말한다(빈 칸과 옛 값은 서로 다른 사실이라 두 문장으로 갈린다).
  const unionColumns = UNION_COLUMNS.filter((c) => !fallbackColumns.includes(c));
  // 배지 글자 — 밝힐 사실만 적는다(계약 1건이면 '계약 1건'이라고 적지 않는다. 대다수 행에
  // 없는 배지가 붙은 이유는 건수가 아니라 폴백·합계 부재·판매 전용이다).
  const badgeText =
    [
      contracts.length > 1 ? `계약 ${contracts.length}건` : null,
      // 짧은 글자도 정본 한 벌 — 고객사 상세 헤더가 같은 배지를 붙인다(두 화면이 같은
      // 고객사를 다른 단어로 부르지 않게). 이유별로 갈리는 것도 그 정본이 한다.
      noReturnReason ? termBasisNoReturnLabel(noReturnReason) : null,
      isFallback ? '진행 중 계약 없음' : null,
      totals ? null : '합계 없음',
    ]
      .filter(Boolean)
      .join(', ') || '계약 기준';
  // 합친 칸이 무엇을 합한 값인가 — 짐작 대신 서버가 실은 근거(basis · 센 계약)로 말한다.
  const unionBasisText = !totals
    ? '합계를 받지 못해 비어 있습니다'
    : isFallback
      ? `진행 중 계약이 없어 최신 계약 ${countedCount}건만 센 값입니다`
      : `진행 중 계약 ${countedCount}건을 합친 값입니다`;

  // 배지 아이콘화(26-08-06 오너 요구) — 글자 배지가 한 화면에 십수 개 반복되면 소음이라,
  // 트리거를 작은 원형 글자 하나로 접는다. 설명은 호버(DisclosureTip 이 원래 호버로 열린다)와
  // 클릭 패널이 그대로 한다. 오너 결정: 시트만(고객사 상세 헤더 배지는 그대로 — 거긴 하나뿐).
  //   ! = 비어 있거나 대신 센 것(진행 중 계약 없음 · 합계 없음) — 봐야 할 것.
  //   ? = 셈법이 다를 뿐 정상(계약 N건 · 판매—반납 없음) — 알아두면 좋은 것.
  // 둘 다 해당하면 ! 하나만 — 아이콘을 두 개 붙이면 지금의 소음이 되돌아온다.
  // 글자는 ASCII 라 글리프 가드(이모지 금지)와 무관하다. badgeText 는 패널 첫 줄과
  // aria(panelLabel)로 옮겨 사실이 사라지지 않는다.
  const isAlert = isFallback || !totals;

  return (
    <DisclosureTip
      panelId={`cm-multi-contract-panel-${rowId}`}
      panelLabel={`${badgeText}, 칸별 기준 안내`}
      triggerTestId="cm-multi-contract-badge"
      triggerStyle={isAlert ? multiContractIconAlert : multiContractIconInfo}
      panelStyle={multiContractPanel}
      trigger={isAlert ? '!' : '?'}
    >
      <p style={{ margin: '0 0 6px', fontWeight: 700 }} data-testid="cm-multi-contract-facts">
        {badgeText}
      </p>
      <p style={{ margin: '0 0 6px' }}>
        {contracts.length > 1
          ? `이 고객사는 계약이 ${contracts.length}건입니다. 시트의 칸은 계약마다 기준이 갈립니다.`
          : '시트의 칸이 무엇을 보고 있는지 알려 드립니다.'}
      </p>
      {/* 다계약 표시 차수3(26-08-05) — 판매 전용은 **정상 상태**다(경고 색을 쓰지 않는다).
          빈 네 칸의 이유를 사실로 적는다 — 서버가 그 이유를 내려주므로 화면이 짐작하지 않는다.
          정정2(26-08-06) — 첫 문장은 **문구 정본 한 벌**(termBasisNoReturnExplanation)이다.
          고객사 상세 헤더의 '판매 — 반납 없음' 배지가 같은 문장을 제목으로 달고 있어서, 두
          자리에 따로 적으면 한쪽만 고쳐진다. 뒤 문장은 이 시트에만 있는 사실이다(상세 헤더에는
          '진행 상태' 칸이라는 개념이 없다). */}
      {/* 정정5(26-08-06) — 뒤 문장의 '단계'가 이번 차수에 **뒤처졌다**: 서버가 이 칸에 '종료'를
          새로 넣었는데(판매 전용 계약이 만기·해지된 고객사) 그 값은 배정·설치의 한 단계가
          아니다. 무엇을 말하는 칸인지는 이제 정본 한 벌(STATUS_BASIS_STAGE_TEXT)이 적는다 —
          고객사 상세 헤더의 같은 설명이 같은 글자를 쓴다. */}
      {noReturnReason && (
        <p style={{ margin: '0 0 6px' }}>
          {termBasisNoReturnExplanation(noReturnReason, joinColumnNames(TERM_BASIS_COLUMNS))}{' '}
          {joinColumnNames(STATUS_BASIS_COLUMNS)} 칸은 그 판매 계약의 {STATUS_BASIS_STAGE_TEXT}를
          말합니다.
        </p>
      )}
      {isFallback && (
        <p style={{ margin: '0 0 6px', color: 'var(--fg-danger)' }}>
          진행 중인 계약이 없습니다. 합계와 대수는 가장 최근 계약 한 건만 세었습니다.
        </p>
      )}
      {!totals && (
        <p style={{ margin: '0 0 6px', color: 'var(--fg-danger)' }}>
          합계를 아직 받지 못해서 금액 다섯 칸과 계약 대수를 비워 두었습니다. 진행 중인 계약만 세는
          새 기준으로만 채우고, 예전 계산으로는 되돌리지 않습니다.
        </p>
      )}
      {/* 다계약 표시 차수3(26-08-05) — 기본 정렬('처음 계약일 순')이 쓰는 값. 계약이 여럿이면
          시트의 '계약일' 칸(최신 계약)과 다른 날이라, 이것을 적지 않으면 운영자가 "왜 이
          순서인가"를 알 수 없다. */}
      {contracts.length > 1 && firstContractedAt !== undefined && (
        <p style={{ margin: '0 0 6px', color: 'var(--fg-muted)' }}>
          처음 계약일은 {formatDate(firstContractedAt)}이고, 기본 정렬이 쓰는 값입니다. 시트의{' '}
          {COLUMN_LABEL.contractedAt} 칸은 최신 계약 기준이라 다를 수 있습니다.
        </p>
      )}
      {contracts.map((c) => {
        const stage = formatContractHandoff(c.handoffStatus) ?? c.handoffStatus;
        // 다계약 표시 차수3 정정(26-08-06) — 판매 전용 계약은 이 줄에서도 반납일·D-Day 를
        // 말하지 않는다. 네 칸 툴팁과 **같은 표기 조각**을 쓴다: 한 행의 두 안내가 같은 계약을
        // 두고 다른 말을 하면 운영자는 어느 쪽이 사실인지 알 수 없고, 판매만 있는 고객사에서는
        // 바로 위 문단("판매는 … 반납이라는 사건이 없습니다")과 이 줄이 정면으로 어긋났다.
        const endDateText = endDateFieldText(COLUMN_LABEL.endDate, c, isSaleOnlySummary(c));
        // 이 계약이 합계에 들어갔는가 — 서버가 실제로 센 계약 목록으로 판정한다
        // (화면이 상태를 다시 판정하면 판정 함수가 두 벌이 된다).
        const counted = totals ? totals.countedContractIds.includes(c.id) : null;
        // 계약 형태는 그 계약의 줄을 접는다 — 시트 '계약 형태' 칸과 같은 호출 규약
        // (foldContractTypeForDisplay 한 벌)이라 두 자리가 다른 단어를 말하지 않는다.
        const kind = foldContractTypeForDisplay(c.contractType, c.robots ?? []);
        // 다계약 표시 차수3(26-08-05) — 네 칸의 기준은 시트가 실제로 쓰는 판정 결과로 표시한다
        // (여기서 isSoonest 를 다시 읽으면, 구버전 서버 폴백에서 시트와 안내가 서로 다른 계약을
        //  가리킬 수 있다). 진행 상태는 서버가 따로 표시해 내리는 계약이고(isStatusBasis),
        // 그 칸을 내리지 않는 구버전에서는 종전대로 임박 계약을 본다.
        const isTermBasis = termBasis.facts ? c.id === termBasis.facts.id : false;
        // 정정2(26-08-06) — 이 폴백 규칙도 정본 한 벌이다(고객사 상세 헤더가 같은 규칙으로
        // 배지의 기준 계약을 찾는다 — 두 화면이 다른 계약을 '상태의 기준'이라 부르지 않게).
        const isStatusBasis = isStatusBasisContract(c);
        const basisColumns = [
          ...(isTermBasis ? TERM_BASIS_COLUMNS : []),
          ...(isStatusBasis ? STATUS_BASIS_COLUMNS : []),
          ...(c.isPrimary ? PRIMARY_BASIS_COLUMNS : []),
        ];
        return (
          <div key={c.id} style={multiContractPanelRow}>
            <div style={{ fontWeight: 600 }}>
              {c.humanId} · {kind} · {c.totalContractQty}대 · {stage}
              {counted === null ? '' : counted ? ' · 합계에 셈' : ' · 합계에서 빠짐'}
            </div>
            {/* 개선 9건 차수2g(26-08-04) — 빠진 이유는 **무엇을 셌는지(basis)** 에 따라
                다르다. 폴백(진행 중 계약이 하나도 없음)에서는 센 계약도 진행 중이 아니다 —
                거기서 '진행 중이 아니어서 뺐다'고 적으면 바로 위 문장("진행 중인 계약이
                없습니다 — 최신 계약 한 건만 세었습니다")과 모순되고, 그 이유가 참이라면 센
                계약도 빠져야 한다. 폴백에서 빠진 진짜 이유는 최신 등록 계약이 아니라는 것뿐이다. */}
            {counted === false && (
              <div style={{ color: 'var(--fg-muted)' }}>
                {isFallback
                  ? '가장 최근에 등록한 계약이 아니어서 금액과 대수 합계에서 뺐습니다.'
                  : '진행 중인 계약이 아니어서 금액과 대수 합계에서 뺐습니다.'}
              </div>
            )}
            <div style={{ color: 'var(--fg-muted)' }}>
              {COLUMN_LABEL.contractedAt} {formatDate(c.contractedAt)} ·{' '}
              {COLUMN_LABEL.installCompletedAt} {formatDate(c.installCompletedAt)} · {endDateText}
            </div>
            {basisColumns.length > 0 && (
              <div style={{ color: 'var(--fg-brand)' }}>
                이 계약이 기준인 칸: {joinColumnNames(basisColumns)}
                {c.isPrimary ? '. 편집 창이 고치는 계약입니다.' : ''}
              </div>
            )}
          </div>
        );
      })}
      <p style={{ margin: '6px 0 0', color: 'var(--fg-muted)' }}>
        전 계약을 합쳐 말하는 칸: {joinColumnNames(unionColumns)}. {unionBasisText}.
      </p>
      {fallbackColumns.length > 0 && (
        <p style={{ margin: '6px 0 0', color: 'var(--fg-danger)' }}>
          아직 합치지 못한 칸: {joinColumnNames(fallbackColumns)}. 서버가 전체 계약을 합친 값을 아직
          보내 주지 않아 최신 계약 1건만 보고 있습니다.
        </p>
      )}
    </DisclosureTip>
  );
}

// PDF §Phase B 페이지 2 §3 — 진행 상태 inline edit cell.
// 7 개 status 옵션을 모두 노출. select 의 value 가 현재 status (manual
// override 적용된 값). 권한 없으면 read-only pill.
// round6 §1-2/R3 — 수동 고정(pinned) 중이면 '고정' 배지를 표시하고, select 에
// '자동 계산으로 되돌리기' 옵션을 노출한다(선택 시 override 해제 = null).
const AUTO_STATUS_VALUE = '__auto__' as const;

const STATUS_SELECT_OPTIONS: CustomerMasterStatus[] = [
  'undefined',
  'in_negotiation',
  'contracted',
  'install_ready',
  'install_done',
  'return_planned',
  'retired',
];

// round8 (요구 2b) — 진행 상태 칩/셀렉트 폭을 라벨 글자수와 무관하게 균일화.
// 기존엔 width 지정이 없어 '계약 체결중'(5자)·'미정'(2자) 등 라벨 길이에 따라 칩
// 폭이 들쭉날쭉해 "진행 바 길이가 안 맞는다"로 오인됐다(진행률 개념은 코드에 없음).
// read-only span·editable select 두 경로에 동일 최소폭 + 가운데정렬을 적용해
// 어떤 상태를 골라도 폭이 고정되게 한다. select 은 드롭다운 화살표 여유폭을 더한다.
// round9 (요구 2) — `minWidth`(하한)는 pinned 행에서 여전히 불균일했다: native
// <select>는 가장 넓은 <option> 기준으로 auto-size 하는데, pinned 행에만 추가되는
// '자동 계산으로 되돌리기'(9자) 옵션이 최장이 되어 그 행만 부풀었다. 진짜
// 고정폭(`width`)으로 전환 + overflow 처리로 pinned 여부와 무관하게 폭을 고정한다.
const STATUS_CHIP_WIDTH = 100;
const STATUS_CHIP_SELECT_WIDTH = STATUS_CHIP_WIDTH + 18; // 드롭다운 화살표 여유폭

function StatusEditCell({
  status,
  pinned,
  color,
  canEdit,
  busy,
  onChange,
}: {
  status: CustomerMasterStatus;
  pinned: boolean;
  color: { fg: string; bg: string; border: string };
  canEdit: boolean;
  busy: boolean;
  onChange: (status: CustomerMasterStatus | null) => void;
}) {
  const pinBadge = pinned ? (
    <span
      title="진행 상태를 직접 정해 두어서 로봇 배치 같은 자동 계산이 반영되지 않습니다. '자동 계산으로 되돌리기'를 고르면 풀립니다."
      style={{
        marginLeft: 4,
        padding: '1px 5px',
        borderRadius: 999,
        border: '1px solid var(--border-strong)',
        background: 'var(--bg-sunken)',
        color: 'var(--fg-muted)',
        fontSize: 10,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      고정
    </span>
  ) : null;

  if (!canEdit) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <span
          style={{
            display: 'inline-block',
            boxSizing: 'border-box',
            width: STATUS_CHIP_WIDTH,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'center',
            padding: '2px 8px',
            borderRadius: 999,
            border: `1px solid ${color.border}`,
            background: color.bg,
            color: color.fg,
            fontSize: 11,
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          {CUSTOMER_MASTER_STATUS_LABEL[status]}
        </span>
        {pinBadge}
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <StatusDropdown
        status={status}
        pinned={pinned}
        color={color}
        busy={busy}
        onChange={onChange}
      />
      {pinBadge}
    </span>
  );
}

// round9 (요구 3) — native <select> 의 드롭다운 팝업은 Chromium 이 옵션 정렬을
// 좌측 고정으로 렌더한다(팝업 스크린샷으로 실측 확인 — select 의 text-align CSS
// 가 옵션 목록엔 전파되지 않음). 값·옵션 모두 중앙정렬이라는 요구를 native 로는
// 만족시킬 수 없어, 옵션까지 직접 그리는 커스텀 드롭다운으로 승격한다
// (EntityPicker.tsx 의 role=listbox + outside-click-close 패턴 재사용).
function StatusDropdown({
  status,
  pinned,
  color,
  busy,
  onChange,
}: {
  status: CustomerMasterStatus;
  pinned: boolean;
  color: { fg: string; bg: string; border: string };
  busy: boolean;
  onChange: (status: CustomerMasterStatus | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onDocMouseDown);
    return () => window.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // listbox 오픈 시 현재 선택된 옵션으로 포커스 이동 — 화살표 키 탐색 시작점.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const current = listRef.current.querySelector<HTMLButtonElement>('[aria-selected="true"]');
    (current ?? listRef.current.querySelector('button'))?.focus();
  }, [open]);

  const options: Array<{ value: CustomerMasterStatus | typeof AUTO_STATUS_VALUE; label: string }> =
    [
      ...(pinned ? [{ value: AUTO_STATUS_VALUE, label: '자동 계산으로 되돌리기' }] : []),
      ...STATUS_SELECT_OPTIONS.map((s) => ({ value: s, label: CUSTOMER_MASTER_STATUS_LABEL[s] })),
    ];

  function pick(value: CustomerMasterStatus | typeof AUTO_STATUS_VALUE): void {
    setOpen(false);
    triggerRef.current?.focus();
    if (value === AUTO_STATUS_VALUE) {
      onChange(null); // override 해제 → 자동 계산 복원
      return;
    }
    if (value !== status) onChange(value);
  }

  const title = pinned
    ? '진행 상태를 직접 정해 두었습니다. 자동 계산으로 되돌리거나 다른 상태로 바꿀 수 있습니다.'
    : '진행 상태 변경: 직접 고르면 자동 계산보다 먼저 적용됩니다';

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={triggerRef}
        type="button"
        disabled={busy}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={title}
        onClick={() => setOpen((o) => !o)}
        style={{
          boxSizing: 'border-box',
          width: STATUS_CHIP_SELECT_WIDTH,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          padding: '2px 6px',
          borderRadius: 999,
          border: `1px solid ${color.border}`,
          background: color.bg,
          color: color.fg,
          fontSize: 11,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {CUSTOMER_MASTER_STATUS_LABEL[status]}
        </span>
        <span aria-hidden="true" style={{ fontSize: 9, lineHeight: 1 }}>
          ▾
        </span>
      </button>
      {open && (
        <div
          ref={listRef}
          role="listbox"
          aria-label={title}
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setOpen(false);
              triggerRef.current?.focus();
              return;
            }
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              const buttons = Array.from(listRef.current?.querySelectorAll('button') ?? []);
              const idx = buttons.indexOf(document.activeElement as HTMLButtonElement);
              const next =
                e.key === 'ArrowDown'
                  ? Math.min(idx + 1, buttons.length - 1)
                  : Math.max(idx - 1, 0);
              buttons[next]?.focus();
            }
          }}
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: 2,
            minWidth: STATUS_CHIP_SELECT_WIDTH,
            zIndex: 20,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-md)',
            overflow: 'hidden',
          }}
        >
          {options.map((opt) => (
            <button
              type="button"
              key={opt.value}
              role="option"
              aria-selected={opt.value === status}
              onClick={() => pick(opt.value)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'center',
                padding: '6px 10px',
                background: opt.value === status ? 'var(--bg-sunken)' : 'transparent',
                color: 'var(--fg-default)',
                border: 'none',
                borderBottom: '1px solid var(--border-subtle)',
                cursor: 'pointer',
                fontSize: 11,
                whiteSpace: 'nowrap',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// styles
// ============================================================

const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
  whiteSpace: 'nowrap',
};
const thead: CSSProperties = {
  position: 'sticky',
  top: 0,
  background: 'var(--bg-sunken)',
  zIndex: 10,
};
const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  borderBottom: '1px solid var(--border-subtle)',
  background: 'var(--bg-sunken)',
};
const tr: CSSProperties = {
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = {
  padding: '6px 10px',
  verticalAlign: 'middle',
  color: 'var(--fg-default)',
};
const tdCenter: CSSProperties = { ...td, textAlign: 'center' };
const tdMono: CSSProperties = { ...td, fontFamily: 'var(--font-mono)' };
const tdEmphasis: CSSProperties = { ...td, fontWeight: 600 };
const groupHeaderTd: CSSProperties = {
  padding: '10px 12px',
  background: 'var(--bg-sunken)',
  borderTop: '2px solid var(--border-default)',
  borderBottom: '1px solid var(--border-subtle)',
  fontSize: 13,
};
const emptyCell: CSSProperties = {
  padding: 40,
  textAlign: 'center',
  color: 'var(--fg-muted)',
};
const linkStyle: CSSProperties = {
  color: 'var(--fg-brand)',
  textDecoration: 'none',
};
const toolInput: CSSProperties = {
  flex: '0 1 260px',
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
};
const toolSelect: CSSProperties = {
  height: 32,
  padding: '0 8px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 12,
};
// 이슈1e — 삭제된 고객사 라이프사이클 UI 스타일.
const includeDeletedLabel: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  color: 'var(--fg-muted)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const deletedBadge: CSSProperties = {
  marginLeft: 6,
  padding: '1px 6px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  border: '1px solid var(--border-danger-saturated, var(--fg-danger))',
  fontSize: 10,
  fontWeight: 600,
};
// 계획서 요구 2a §1(26-08-21) — 반납일 칸의 '만료' 배지. 색은 D-Day 의 지남(D+N) 경고와
// 같은 danger 계열 — 두 신호가 같은 사실(기준 계약이 만기를 지났다)을 말하기 때문이다.
// 글자('만료')가 함께 있으므로 색만으로 뜻을 전하지 않는다(화면 규칙).
const expiredBadge: CSSProperties = {
  marginLeft: 6,
  padding: '1px 6px',
  borderRadius: 999,
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  border: '1px solid var(--border-danger-saturated, var(--fg-danger))',
  fontSize: 10,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};
// 계획서 요구 2b 선택 A(26-08-21) — 지역/국가 프리셋 칩. 색·모양은 이 시트의 활성 필터
// 셀(filterCellStyle active)과 같은 어휘를 쓴다(새 장식 금지 — 브랜드색 + 침몰 배경).
const presetChip: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  padding: '0 5px 0 10px',
  height: 32,
  color: 'var(--brand)',
  background: 'var(--bg-sunken)',
  border: '1px solid var(--brand)',
  borderRadius: 999,
  whiteSpace: 'nowrap',
};
const presetChipClear: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  fontSize: 12,
  cursor: 'pointer',
  padding: '2px 4px',
  lineHeight: 1,
};
// 개선 9건 차수2c(26-08-04) §5 — 다계약 배지(반납일 칸). 중립 톤 — 경고가 아니라
// "이 칸은 계약 한 건만 말한다"는 안내다.
// 개선 9건 차수2e(26-08-04) ⑤ — span 에서 button 으로 승격(키보드·터치로 열린다).
// `fontFamily: inherit` 은 종전 span 이 칸에서 물려받던 글꼴을 그대로 유지하려는 것이다
// (버튼은 UA 기본 글꼴로 떨어지므로 명시하지 않으면 배지 모양이 바뀐다).
// 개선 9건 차수2f(26-08-04) — 합계를 못 받았다는 표 단위 안내. 경고 색을 쓰되 배너가
// 표를 밀어내지 않도록 한 줄 높이 여백만 쓴다(정상 상태에서는 렌더되지 않는다).
const totalsMissingNotice: CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--border-warning-saturated)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-default)',
  fontSize: 12,
  lineHeight: 1.5,
};
// 배지 아이콘화(26-08-06) — 글자 배지 대신 작은 원형 글자 하나. 크기를 고정해 행 높이가
// 배지 유무·길이에 따라 흔들리지 않는다. 색은 뜻을 따른다:
//   ? (중립) — 셈법이 다를 뿐 정상. 경고색을 쓰면 목록 대부분이 붉어져 신호가 죽는다.
//   ! (주황) — 비어 있거나 대신 센 것. 봐야 할 것만 눈에 띈다.
const multiContractIconBase: CSSProperties = {
  marginLeft: 6,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  borderRadius: '50%',
  fontFamily: 'inherit',
  fontSize: 10,
  fontWeight: 800,
  lineHeight: 1,
  cursor: 'help',
  padding: 0,
};
const multiContractIconInfo: CSSProperties = {
  ...multiContractIconBase,
  background: 'var(--bg-subtle)',
  color: 'var(--fg-muted)',
  border: '1px solid var(--border-default)',
};
const multiContractIconAlert: CSSProperties = {
  ...multiContractIconBase,
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-on-warning-tint)',
  border: '1px solid var(--fg-on-warning-tint)',
};
// 배지가 여는 안내 패널. 표가 nowrap + mono 라 문장 표기를 명시적으로 되돌린다.
// z-index 는 sticky thead(10) 보다 위 — StatusDropdown 과 같은 값.
const multiContractPanel: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  zIndex: 20,
  width: 360,
  maxWidth: '70vw',
  padding: '10px 12px',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  boxShadow: 'var(--shadow-md)',
  whiteSpace: 'normal',
  fontFamily: 'var(--font-sans)',
  fontSize: 11,
  lineHeight: 1.5,
  fontWeight: 400,
  textAlign: 'left',
  color: 'var(--fg-default)',
  cursor: 'auto',
};
const multiContractPanelRow: CSSProperties = {
  padding: '6px 0',
  borderTop: '1px solid var(--border-subtle)',
};
// 다계약 표시 차수3(26-08-05) — 네 칸 툴팁. 여닫는 기계는 배지와 같은 한 벌이고 모양만 다르다:
// 칸 안의 값 **그 자체**가 여는 자리이므로 버튼처럼 보이지 않아야 한다(글꼴·색을 칸에서
// 물려받고 점선 밑줄로 "열린다"만 알린다). 계약이 1건인 행에는 이 버튼이 아예 없다.
const termBasisTipTrigger: CSSProperties = {
  font: 'inherit',
  color: 'inherit',
  background: 'transparent',
  border: 'none',
  padding: 0,
  textDecoration: 'underline dotted',
  textDecorationColor: 'var(--border-strong)',
  textUnderlineOffset: 3,
  cursor: 'help',
};
// 패널은 배지 패널과 같은 상자(표가 nowrap + mono 라 문장 표기를 되돌린다)이고 폭만 좁다 —
// 담는 것이 네 항목뿐이다(무엇을 담지 않는지는 TermBasisTip 주석).
const termBasisTipPanel: CSSProperties = { ...multiContractPanel, width: 320 };
const restoreActionBtn: CSSProperties = {
  padding: '3px 8px',
  background: 'transparent',
  color: 'var(--fg-brand)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  cursor: 'pointer',
};
const purgeActionBtn: CSSProperties = {
  padding: '3px 8px',
  background: 'transparent',
  color: 'var(--fg-danger)',
  border: '1px solid var(--border-danger-saturated, var(--fg-danger))',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  cursor: 'pointer',
};
// round9 (요구 8) — RobotsListPage 의 행별 편집 연필(iconBtn)과 동형.
const editActionBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  padding: 0,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-btn)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
};
